/*
  Ever Leaf — Mobile Immersive Product Stage.

  Mobile-only presentation layer. Reparents (never clones/duplicates) the
  SAME real Rating/Title/Price/Description/Personalizer-Heading/
  Interaction-Hints/View-Toggle nodes (independently "Mobile Y from Top"),
  the SAME real Upload/Dedication-Line1/Dedication-Line2/Materials controls
  (into the Configuration Carousel, "Mobile Y from Bottom"), and the SAME
  real <product-form> — Add to Cart independently "Mobile Y from Bottom" —
  into the existing `.ever-leaf-product__visual` column, which becomes the
  "Mobile Stage" positioning context. Desktop's DOM/CSS is completely
  untouched — this script no-ops entirely outside the existing
  `(max-width: 749px)` breakpoint (matchMedia), matching the theme's
  one-breakpoint convention.

  Hard invariants preserved (see scratchpad/feature-mobile-immersive-stage/
  architecture-note.md for the full rationale):
  - Exactly one <product-form>, one button[name="add"], one input[name="id"],
    one EverLeafPersonalizerState — nothing here creates a second of any of
    them. The ATC button carries `form="{{ product_form_id }}"` (added in
    snippets/ever-leaf-product-purchase.liquid) so moving its wrapper div
    out of the <form>'s own DOM subtree still submits the one real form.
  - Carousel active-page is presentation-only state (a plain JS variable +
    a `data-active-page` attribute) — never written to
    EverLeafPersonalizerState, never a cart/order property.
  - Reparenting is idempotent (checked via a WeakSet) and reversible: each
    moved node gets a fixed placeholder marker left at its ORIGINAL DOM
    position (see the `placeholders` WeakMap below), so restoration is
    always correct regardless of what order nodes are restored in — Theme
    Editor's live Desktop<->Mobile preview toggle restores Desktop's
    original DOM exactly.
*/
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';
  var mql = window.matchMedia(MOBILE_QUERY);

  // REGRESSION CORRECTION PASS — the previous parent+nextSibling restoration
  // model was order-dependent: if node A's recorded nextSibling was node B,
  // and B was ALSO still moved into the stage at the moment A gets restored,
  // `insertBefore(A, B)` would fail (B is not currently a child of A's
  // recorded parent) and silently fall back to appendChild — exactly the
  // observed Desktop regression (Title's real nextSibling was Price, also a
  // moved node, so Title always landed at the very end). Fixed with a
  // lightweight placeholder-node marker left at each node's ORIGINAL DOM
  // position at move time. The marker never itself moves, so restoring a
  // node is always a single `parent.insertBefore(node, marker)` relative to
  // a fixed point — correct regardless of what order other nodes are
  // restored in. Markers are removed once their node is back in place. No
  // functional control is ever cloned; markers are empty, non-rendering
  // comment nodes with zero effect on layout/selectors/CSS.
  var placeholders = new WeakMap(); // node -> its marker comment node
  var movedNodes = [];

  function moveTo(node, destination) {
    if (!node || !destination) return;
    if (!placeholders.has(node)) {
      var marker = document.createComment('ever-leaf-mobile-stage-placeholder');
      node.parentNode.insertBefore(marker, node);
      placeholders.set(node, marker);
    }
    if (node.parentNode !== destination) destination.appendChild(node);
    if (movedNodes.indexOf(node) === -1) movedNodes.push(node);
  }

  function restoreAll() {
    movedNodes.forEach(function (node) {
      var marker = placeholders.get(node);
      if (!marker || !marker.parentNode) return;
      marker.parentNode.insertBefore(node, marker);
      marker.parentNode.removeChild(marker);
      placeholders.delete(node);
    });
  }

  // REFERENCE RESPONSIVE GEOMETRY. Canonical Mobile design canvas is the
  // iPhone 13 Pro Max portrait, 428x926 CSS px. Merchant pixel settings are
  // reference pixels; CSS multiplies them by --ever-leaf-layout-scale
  // (= min(width/428, height/926), clamped) published here on each product
  // root. Raw merchant values stay in inline custom properties and are never
  // rewritten, so nothing is ever scaled twice. One shared resize listener
  // (not one per root) avoids listener build-up across Theme Editor reloads.
  var REFERENCE_WIDTH = 428;
  var REFERENCE_HEIGHT = 926;
  var SCALE_MIN = 0.65;
  var SCALE_MAX = 1.15;
  var WIDTH_THRESHOLD_PX = 2;
  var stageRoots = new Set();
  var lastGeometryWidth = 0;

  function readViewport() {
    var vv = window.visualViewport;
    return {
      width: (vv && vv.width) || window.innerWidth,
      height: (vv && vv.height) || window.innerHeight
    };
  }

  function getMobileReferenceGeometry() {
    var vp = readViewport();
    var widthScale = vp.width / REFERENCE_WIDTH;
    var heightScale = vp.height / REFERENCE_HEIGHT;
    var layoutScale = Math.max(SCALE_MIN, Math.min(SCALE_MAX, Math.min(widthScale, heightScale)));
    return {
      referenceWidth: REFERENCE_WIDTH,
      referenceHeight: REFERENCE_HEIGHT,
      viewportWidth: vp.width,
      viewportHeight: vp.height,
      widthScale: widthScale,
      heightScale: heightScale,
      layoutScale: layoutScale
    };
  }

  // MOBILE STAGE HEIGHT — GEOMETRY BUG FIX (Pass 4, Item 8). CONFIRMED bug:
  // the old formula (926px reference canvas * layoutScale * pct/100) made
  // "100%" mean 100% of the *reference* canvas scaled to fit the viewport —
  // NOT 100% of the space actually available below whatever renders before
  // the Stage. At the 428x926 reference viewport the real pre-Stage offset
  // (measured) is 108px, so a 100% Stage (926px tall) always ran 108px past
  // the bottom of the viewport, pushing the Visualizer ~54px too low —
  // which is also why merchants had already gravitated to 85-90% as the
  // "correct-looking" value instead of 100%.
  //
  // NEW SEMANTICS: Stage Height % is now % of (current viewport height -
  // the Stage's own current pre-Stage offset), so 100% genuinely reaches
  // the true bottom of the visible viewport, never past it.
  //
  // Offset source: first tried this theme's own live --header-height custom
  // property (sections/header.liquid, already relied on elsewhere for the
  // same "compensate for header space" purpose) — measured WRONG on the
  // real page (69px) against the Stage's actual, verified-by-direct-
  // measurement offset (108px: confirmed via getBoundingClientRect() on
  // the header/product-section/Stage all agreeing exactly at 108, with a
  // real ~39px gap --header-height simply never accounted for). Rather
  // than depend on a value proven stale/unreliable here, this instead
  // measures the Stage's OWN real, current document-flow position
  // directly: `stage.getBoundingClientRect().top`, corrected by the
  // current scroll offset (+ window.scrollY) so the result is the Stage's
  // position AS IF the page were scrolled to the top — a fixed,
  // document-flow quantity, unaffected by however much the page happens to
  // be scrolled at the moment this runs. This is NOT the "raw
  // getBoundingClientRect().top that shrinks with scroll" the brief warns
  // against (that would be using the viewport-relative value AS-IS); the
  // scrollY correction is exactly what removes that scroll-dependence.
  // Correct regardless of what's actually contributing to the offset
  // (header, announcement bar, section padding, anything) since it reads
  // the real rendered position directly rather than trying to enumerate or
  // sum the individual contributors. Safe against circularity: the Stage's
  // own `top` position (normal block flow, no transform) is governed
  // entirely by content BEFORE it, never by its own height, so measuring
  // this does not depend on the height formula being computed from it.
  //
  // Viewport source: window.visualViewport.height (via the SAME
  // readViewport() this file already uses for layoutScale), not 100dvh/
  // 100svh/100vh — kept as ONE viewport-measurement system, not two
  // independent/possibly-disagreeing ones, and visualViewport is the more
  // accurate source on iOS Safari (accounts for the on-screen keyboard/
  // browser chrome the way legacy vh units do not).
  //
  // layoutScale is DELIBERATELY NOT part of this formula. layoutScale
  // exists to convert *reference-canvas* pixel settings (Y-from-top
  // offsets, View Frame spacing, etc. — all authored against the 428x926
  // canvas) into real device pixels. The new available-height figure below
  // is already computed directly from REAL, CURRENT viewport/offset
  // pixels — applying layoutScale to it again would double-scale it. Every
  // OTHER reference-pixel setting keeps using layoutScale exactly as
  // before; only the Stage height's own formula is self-contained.
  function getStageAvailableHeight(stage) {
    var vp = readViewport();
    var documentRelativeTop = stage.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0);
    return Math.max(0, vp.height - documentRelativeTop);
  }

  function applyMobileReferenceGeometry(root) {
    var g = getMobileReferenceGeometry();
    root.style.setProperty('--ever-leaf-layout-scale', String(+g.layoutScale.toFixed(4)));
    var stage = root.querySelector('.ever-leaf-product__visual[data-ever-leaf-mobile-stage]');
    if (stage) {
      root.style.setProperty('--ever-leaf-stage-available-height', String(+getStageAvailableHeight(stage).toFixed(2)) + 'px');
    }
    lastGeometryWidth = g.viewportWidth;
    return g;
  }

  function clearMobileReferenceGeometry(root) {
    root.style.removeProperty('--ever-leaf-layout-scale');
    root.style.removeProperty('--ever-leaf-stage-available-height');
  }

  function refreshAllGeometry(force) {
    var w = readViewport().width;
    // Height-only changes (Mobile Safari address/tab bar) are ignored: only a
    // material WIDTH change (device/preview resize, rotation) recomputes.
    if (!force && Math.abs(w - lastGeometryWidth) < WIDTH_THRESHOLD_PX) return;
    stageRoots.forEach(function (r) {
      if (!r.isConnected) { stageRoots.delete(r); return; }
      if (r.hasAttribute('data-ever-leaf-mobile-stage-active')) applyMobileReferenceGeometry(r);
    });
    lastGeometryWidth = w;
  }
  window.addEventListener('resize', function () { refreshAllGeometry(false); });
  window.addEventListener('orientationchange', function () { setTimeout(function () { refreshAllGeometry(true); }, 250); });

  // THEME EDITOR <-> REAL MOBILE FRAMING PARITY PASS — root cause of the
  // reported discrepancy: getStageAvailableHeight() above measures the
  // Stage's real document-flow top ONCE, at DOMContentLoaded (via
  // activateMobile() -> applyMobileReferenceGeometry()), and nothing
  // re-measures it afterward unless the VIEWPORT WIDTH later changes
  // (resize/orientationchange, above) — a real settled-height reflow
  // (async header content, a font swap that changes text metrics, an
  // announcement bar/app embed that finishes rendering after first paint)
  // never triggers a recompute on the plain storefront, because none of
  // that changes viewport WIDTH. In the real Shopify Theme Editor,
  // `shopify:section:load` (gated behind Shopify.designMode, further down
  // this file) incidentally re-runs applyMobileReferenceGeometry() on
  // nearly every editor interaction, which naturally lands on the correct,
  // fully-settled value after the first such reload — masking the exact
  // same bug that a real phone's single, early measurement never
  // self-corrects from. This is a timing gap, not an iframe-vs-real-
  // viewport size difference (readViewport()'s width/height source is
  // already the same visualViewport-first measurement in both places).
  //
  // Fix: two further ONE-SHOT (never repeating, never polling) forced
  // recomputes at well-defined standard lifecycle points, reusing the
  // exact same canonical refreshAllGeometry(true) every other trigger
  // already calls — no new formula, no environment branch, applies
  // identically to Theme Editor and the real storefront alike.
  window.addEventListener('load', function () { refreshAllGeometry(true); });
  if (window.document.fonts && window.document.fonts.ready && typeof window.document.fonts.ready.then === 'function') {
    window.document.fonts.ready.then(function () { refreshAllGeometry(true); });
  }

  // Theme Editor lifecycle fix (Mobile Dock UI Corrections pass) — root
  // cause, diagnosed on the real running theme: a Materials/Color click
  // publishes Dawn's `optionValueSelectionChange` pubsub event, and in the
  // Theme Editor (never on the plain storefront) Shopify's own preview
  // instrumentation can follow that with a lighter-weight refresh that
  // REUSES the existing `.ever-leaf-product` root while replacing its
  // children, instead of the full fresh-root section reload the old
  // `__everLeafStageBound` boolean assumed. That boolean then silently
  // skipped every re-bind forever, leaving the Dock's closures (mql
  // listener, ResizeObservers, IntersectionObserver, the has-photo
  // MutationObserver) pointing at now-detached nodes -> the Dock/Carousel
  // never reappears. Fixed by tracking a per-root TEARDOWN function instead
  // of a boolean: a later initStage() call for an already-bound root tears
  // the old binding down first (removes the stale mql listener, disconnects
  // every observer) and then rebinds fresh against the current DOM - so a
  // real Theme Editor refresh self-heals instead of leaving stale state,
  // and nothing is ever bound twice.
  //
  // A second, harder case (found in QA for this exact fix): when Shopify
  // instead replaces the section with a genuinely NEW root element (rather
  // than reusing the old one), the OLD root is simply gone - nothing ever
  // calls initStage() with that exact reference again, so its teardown
  // above would never run at all. Left alone, its `mql` "change" listener
  // keeps firing forever with CLOSURES over the detached old stage/dock,
  // and because moveRealNodesIn() and restoreAll()/movedNodes are `document.
  // querySelector` / module-level globals (not scoped to one root), that
  // stale listener can reach out and move the CURRENT live product-form into
  // its own detached tree on the very next Mobile<->Desktop resize - making
  // Add to Cart vanish. Fixed the same way `stageRoots` already self-prunes
  // in refreshAllGeometry(): every initStage() call first tears down any
  // OTHER bound root that has fallen out of the live document.
  var boundRoots = new Map();
  /* ---- Active Carousel page memory, per Product Section ----------------
     A Theme Editor setting change re-renders the section: the Carousel
     markup is replaced and initStage() runs again against brand-new nodes,
     so the per-activation `activePage` closure variable starts at 'photo'
     again and the merchant is thrown back to Upload every time they tune a
     Line 1 / Line 2 / Color setting. This Map remembers the last active
     page per SECTION (not globally), so two product sections on one page
     can never read each other's value, and it is deliberately in-memory
     only: a genuine fresh page load starts a customer on Upload as before.
     It holds two short strings per section and registers no listeners or
     subscriptions of any kind, so it cannot contribute to the listener
     accumulation being investigated for the Material Editor freeze. */
  var activePageBySection = new Map();
  var ACTIVE_PAGE_STORE_PREFIX = 'everLeafActivePage:';
  function sectionKeyFor(root) {
    var host = root.closest ? root.closest('.shopify-section, [id^="shopify-section"]') : null;
    return (host && host.id) || (root.getAttribute && root.getAttribute('data-section-id')) || 'ever-leaf-default';
  }
  function pruneActivePageMemory() {
    activePageBySection.forEach(function (_value, key) {
      if (key !== 'ever-leaf-default' && !document.getElementById(key)) activePageBySection.delete(key);
    });
  }
  function initStage(root) {
    boundRoots.forEach(function (teardown, boundRoot) {
      if (boundRoot !== root && !boundRoot.isConnected) {
        teardown();
        boundRoots.delete(boundRoot);
      }
    });
    var existingTeardown = boundRoots.get(root);
    if (existingTeardown) existingTeardown();
    stageRoots.add(root);

    var stage = root.querySelector('.ever-leaf-product__visual[data-ever-leaf-mobile-stage]');
    if (!stage) return;
    var dock = stage.querySelector('[data-ever-leaf-mobile-dock]');
    var slots = {
      heading: dock && dock.querySelector('[data-ever-leaf-dock-slot="heading"]'),
      separator: dock && dock.querySelector('[data-ever-leaf-dock-slot="separator"]'),
      cta: dock && dock.querySelector('[data-ever-leaf-dock-slot="cta"]'),
      price: dock && dock.querySelector('[data-ever-leaf-dock-slot="price"]')
    };
    var carousel = stage.querySelector('[data-ever-leaf-config-carousel]');
    var track = stage.querySelector('[data-ever-leaf-config-carousel-track]');
    var nav = stage.querySelector('[data-ever-leaf-config-carousel-nav]');
    if (!dock || !slots.heading || !slots.cta || !slots.price || !carousel || !track || !nav) return;

    var pages = {
      photo: carousel.querySelector('[data-ever-leaf-config-page="photo"]'),
      line1: carousel.querySelector('[data-ever-leaf-config-page="line1"]'),
      line2: carousel.querySelector('[data-ever-leaf-config-page="line2"]'),
      material: carousel.querySelector('[data-ever-leaf-config-page="material"]')
    };
    var tabs = {};
    // Registered once per closure (not per activate/deactivate cycle) so a
    // resize that toggles Mobile off and back on can never accumulate a
    // second listener on the same `track` element.
    if ('onscrollend' in track) {
      track.addEventListener('scrollend', clearScrollTarget);
    }

    nav.querySelectorAll('[data-ever-leaf-config-tab]').forEach(function (tab) {
      tabs[tab.getAttribute('data-ever-leaf-config-tab')] = tab;
    });

    var pageOrder = ['photo', 'line1', 'line2', 'material'];
    var prevBtn = carousel.querySelector('[data-ever-leaf-config-carousel-prev]');
    var nextBtn = carousel.querySelector('[data-ever-leaf-config-carousel-next]');

    /* Normalized Carousel height: every page shares ONE track height.
       Match Upload = Upload page natural height + 2 x extra; Custom = merchant
       px. Never below the tallest page content (no clipping). The Upload
       measure keeps the max seen so the Dock does not jump between the
       empty and uploaded states of the Upload row. */
    var heightRO = null;
    var uploadSeen = 0;
    function applyCarouselHeight() {
      var mode = dock.getAttribute('data-ever-leaf-carousel-height-mode') || 'match_upload';
      var extra = parseFloat(dock.getAttribute('data-ever-leaf-carousel-height-extra')) || 0;
      var custom = parseFloat(dock.getAttribute('data-ever-leaf-carousel-height-custom')) || 0;
      var tallest = 0;
      pageOrder.forEach(function (key) {
        tallest = Math.max(tallest, pages[key].getBoundingClientRect().height);
      });
      var up = pages.photo.getBoundingClientRect().height;
      uploadSeen = Math.max(uploadSeen, up);
      var target = mode === 'custom' ? custom : uploadSeen + extra * 2;
      var h = Math.ceil(Math.max(target, tallest));
      if (h > 0 && track.style.getPropertyValue('--ever-leaf-carousel-h') !== h + 'px') {
        track.style.setProperty('--ever-leaf-carousel-h', h + 'px');
      }
      // NOTE (Pass 10): the value computed above is deliberately the ONLY
      // vertical size authority for the Carousel envelope, and it is
      // page-INDEPENDENT by construction - `tallest` is a max across all
      // four pages and `target` comes from the mode (Upload reference or
      // the merchant's Custom value), so switching pages cannot change it.
      // Pass 8 Item 2 added a second, active-page-DEPENDENT correction
      // here (`--ever-leaf-carousel-trailing-slack`) to close the gap to
      // the Decorative Separator per page. Measurement (real Chromium,
      // production CSS, real DEV settings) showed that correction was the
      // sole cause of the Dock moving on every page change: track height
      // spread stayed 0.0px in Match Upload AND Custom modes, while
      // Dock/Heading/Tabs/track top moved 7.0px with the correction on and
      // 0.0px with it off - and at larger Custom heights it pulled the
      // separator into a NEGATIVE gap, overlapping the Carousel content.
      // Rolled back per USER priority: a stationary Dock outranks an exact
      // zero separator gap. Carousel -> Separator zero precision is OPEN /
      // NON-BLOCKING; do not reintroduce a dynamic per-page compensation.
    }
    function observeCarouselHeight() {
      if (typeof ResizeObserver !== 'function') { applyCarouselHeight(); return; }
      if (!heightRO) heightRO = new ResizeObserver(applyCarouselHeight);
      pageOrder.forEach(function (key) {
        heightRO.observe(pages[key]);
        Array.prototype.forEach.call(pages[key].children, function (c) { heightRO.observe(c); });
      });
      applyCarouselHeight();
    }
    var activePage = 'photo';
    var observer = null;
    // Transient-flash fix (Mobile Dock UI Corrections pass) — root cause,
    // diagnosed from the code: a DIRECT navigation (tab click, or the
    // Photo->Line 1 auto-advance) scrolls with `scrollIntoView({behavior:
    // 'smooth'})`. Because the pages sit in one fixed DOM order (photo,
    // line1, line2, material), a smooth scroll from Photo/Line 1 straight
    // to Color visually passes OVER Line 2 for a few frames - and the
    // IntersectionObserver below (built for NATIVE SWIPE detection) can't
    // tell a deliberate scroll from a passing-through one, so it "corrects"
    // the active tab to Line 2 for that instant. Fixed by recording the
    // intended destination for every scroll WE started; the observer then
    // ignores every intersection that isn't that exact destination, so only
    // a genuine user-driven swipe (no destination recorded) can move the
    // active tab through an intermediate page.
    var scrollTargetKey = null;
    var scrollSettleTimer = null;

    function clearScrollTarget() {
      scrollTargetKey = null;
      if (scrollSettleTimer) { clearTimeout(scrollSettleTimer); scrollSettleTimer = null; }
    }

    /* ---- Photo-gated Carousel destinations -------------------------------
       Line 1, Line 2 and Color are unreachable until a photo actually
       exists. The gate reads the SAME canonical state everything else on
       this page already uses - the `ever-leaf-has-photo` class on the
       product root, which `stateMO` below is already observing - so there
       is no second hasPhoto concept anywhere.

       The locked pages deliberately stay rendered (never display:none):
       applyCarouselHeight() sizes the shared track from the tallest of all
       four pages, so removing them from layout would change the Carousel's
       outer height between the locked and unlocked states - exactly the
       vertical instability this project just finished eliminating. */
    var sectionKey = sectionKeyFor(root);
    function inDesignMode() {
      return !!(window.Shopify && window.Shopify.designMode);
    }
    /* Pass 12 — why the Pass 11 module-scoped Map alone failed real USER QA.
       The Theme Editor has TWO different ways of applying a setting change:
       it re-renders the section in place (this document keeps running, so
       module state survives - that path did work), or it reloads the whole
       preview document (a brand-new JS realm, so every module variable is
       gone - that path always fell back to Upload). Which one a given
       setting takes is Shopify's choice, not ours, and the merchant reported
       failure while changing MANY different settings - i.e. hitting the
       reload path. The Map is kept as the in-memory fast path and backed by
       sessionStorage, which is the smallest owner that survives a reload:
       tab-scoped (gone when the tab closes), keyed per section so two
       product sections can never contaminate each other, written ONLY in
       design mode so a real customer's storage is never touched, no URL
       mutation, and no listener or subscription of any kind. */
    function rememberActivePage(pageKey) {
      pruneActivePageMemory();
      activePageBySection.set(sectionKey, pageKey);
      if (!inDesignMode()) return;
      try {
        sessionStorage.setItem(ACTIVE_PAGE_STORE_PREFIX + sectionKey, pageKey);
      } catch (e) {
        /* private mode / storage disabled - the in-memory Map still covers
           the in-place re-render path. */
      }
    }
    function readRememberedPage() {
      var fromMemory = activePageBySection.get(sectionKey);
      if (fromMemory) return fromMemory;
      if (!inDesignMode()) return null;
      try {
        return sessionStorage.getItem(ACTIVE_PAGE_STORE_PREFIX + sectionKey);
      } catch (e) {
        return null;
      }
    }
    function recallActivePage() {
      var remembered = readRememberedPage();
      if (!remembered || !pages[remembered]) return 'photo';
      return remembered;
    }
    function hasPhotoNow() {
      return root.classList.contains('ever-leaf-has-photo');
    }
    /* Pass 12 — the photo gate locks CONTENT, never navigation. The customer
       may browse all four pages before uploading anything (that is how they
       discover what personalization is available); it is the personalization
       CONTROLS on Line 1 / Line 2 / Color that stay inert until a photo
       exists. Pass 11's navigation lock (blocked tabs/arrows/swipe and a
       forced return to Upload) was explicitly rejected in USER QA and is
       gone: no page is ever unreachable, and removing a photo no longer
       yanks the customer back to Upload. */
    function applyContentLockState() {
      var locked = !hasPhotoNow();
      // One attribute on the Dock drives every locked visual, so the greying
      // is scoped to the content groups themselves - never the Carousel
      // track, pages, tabs or arrows (see ever-leaf-product.css).
      dock.setAttribute('data-ever-leaf-content-locked', locked ? 'true' : 'false');
      // Real disabling, not a cosmetic overlay: a disabled <input> cannot be
      // focused, cannot receive typing and cannot open the on-screen
      // keyboard; a disabled <button>/<input type=radio> cannot be activated
      // by pointer OR keyboard. Scoped to the controls inside the three
      // gated pages, resolved live because these nodes are reparented.
      ['line1', 'line2', 'material'].forEach(function (key) {
        var host = pages[key];
        if (!host) return;
        host.querySelectorAll('input, button, select, textarea').forEach(function (el) {
          // Never touch a control that something else legitimately disabled
          // (e.g. a sold-out variant): only manage the ones this gate owns.
          if (locked) {
            if (!el.disabled) el.setAttribute('data-ever-leaf-photo-gated', 'true');
          }
          if (el.getAttribute('data-ever-leaf-photo-gated') === 'true') {
            el.disabled = locked;
            if (!locked) el.removeAttribute('data-ever-leaf-photo-gated');
          }
        });
      });
    }
    function refreshArrowState() {
      var idx = pageOrder.indexOf(activePage);
      if (prevBtn) prevBtn.disabled = idx <= 0;
      if (nextBtn) nextBtn.disabled = idx < 0 || idx >= pageOrder.length - 1;
    }

    function setActivePage(pageKey, opts) {
      opts = opts || {};
      if (!pages[pageKey]) return;
      // Every navigation path funnels through here (tab, arrow, native
      // swipe via the IntersectionObserver, upload auto-advance, activation
      // and Theme Editor restoration). Pass 12: no page is ever refused -
      // the photo gate locks page CONTENT, not navigation.
      activePage = pageKey;
      rememberActivePage(pageKey);
      carousel.setAttribute('data-active-page', pageKey);
      pageOrder.forEach(function (key) {
        if (tabs[key]) tabs[key].setAttribute('aria-selected', key === pageKey ? 'true' : 'false');
      });
      // Prev/Next arrows (Pass 3, Item 4) — single source of truth: this
      // runs for EVERY navigation path (direct tab click, arrow click, and
      // a genuine user swipe via the IntersectionObserver below), so the
      // disabled state always matches the real active page no matter how
      // it changed. No wrap: disabled outright at either end.
      refreshArrowState();
      // (Pass 10) The applyCarouselHeight() call that used to run here was
      // added in Pass 8 solely to refresh the per-page trailing slack on
      // navigation. With that correction rolled back, the Carousel height
      // no longer depends on which page is active, so navigation must not
      // trigger a height recompute at all - that is what kept the Dock
      // moving on every tab/arrow press.
      if (opts.scroll !== false) {
        scrollTargetKey = pageKey;
        if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
        // Safety net: if scrollend never fires (older Safari) or the 0.6
        // threshold is never reached (short scroll distance), still release
        // the guard once any real smooth/instant scroll would have settled.
        scrollSettleTimer = setTimeout(clearScrollTarget, 650);
        pages[pageKey].scrollIntoView({ block: 'nearest', inline: 'center', behavior: opts.instant ? 'auto' : 'smooth' });
      }
    }

    function setupObserver() {
      if (observer) observer.disconnect();
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              var key = entry.target.getAttribute('data-ever-leaf-config-page');
              if (!key) return;
              // Mid-programmatic-scroll: only the intended destination may
              // ever become active; every other crossing is ignored outright.
              if (scrollTargetKey) {
                if (key === scrollTargetKey) clearScrollTarget();
                else return;
              }
              if (key !== activePage) setActivePage(key, { scroll: false });
            }
          });
        },
        { root: track, threshold: [0.6] }
      );
      pageOrder.forEach(function (key) {
        if (pages[key]) observer.observe(pages[key]);
      });
    }

    nav.querySelectorAll('[data-ever-leaf-config-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        setActivePage(tab.getAttribute('data-ever-leaf-config-tab'));
      });
    });

    // Prev/Next arrows navigate through the SAME setActivePage() path as a
    // direct tab click — same scroll/snap, same IntersectionObserver sync,
    // same transient-flash guard, same auto-advance interplay. No wrap: a
    // disabled button (see setActivePage's idx check above) never fires.
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        var idx = pageOrder.indexOf(activePage);
        if (idx > 0) setActivePage(pageOrder[idx - 1]);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        var idx = pageOrder.indexOf(activePage);
        if (idx >= 0 && idx < pageOrder.length - 1) setActivePage(pageOrder[idx + 1]);
      });
    }

    // Auto-advance: Photo -> Line 1, ONLY on a genuine new local-photo-ready
    // event, exactly once per event, and only if the customer is currently
    // still on the Photo page (never yanks them away from a later page they
    // already navigated to on purpose — e.g. replacing the photo while
    // reviewing Line 2 must not jump them back to Line 1).
    function onLocalPhotoReady() {
      // This event IS the proof a photo now exists and can land a frame
      // before the `ever-leaf-has-photo` class does, so unlock the content
      // from it directly. The pre-existing "only if still on Photo" rule is
      // unchanged, so replacing a photo while reviewing Line 2 still never
      // yanks anyone back to Line 1.
      applyContentLockState();
      if (mql.matches && activePage === 'photo') {
        setActivePage('line1');
      }
    }
    document.addEventListener('ever-leaf:local-photo-ready', onLocalPhotoReady);

    // FIRST-APPEARANCE-DELAY REMOVAL PASS — split out of one unconditional
    // move so the very first Mobile activation can reparent everything that
    // is SAFE to move immediately (structural targets, none of which any
    // other deferred script resolves via a scoped querySelector) without
    // waiting for the SAME macrotask defer that View Toggle/Hints still
    // need. `includeToggleAndHints` is true for every call EXCEPT the
    // first-activation early pass (see initStage()'s own call site below) -
    // resize/Theme-Editor-reload activations always pass true, since
    // ever-leaf-personalizer.js has certainly already run by then and the
    // race this guards against cannot recur.
    //
    // WHY Toggle/Hints specifically still require the defer, and Rating/
    // Title/Description/Price/Heading/Dock do not: ever-leaf-personalizer.js
    // resolves `viewToggleButton`/the Hints elements via `el.querySelector(...)`
    // scoped to its OWN <ever-leaf-personalizer> custom element (assets/
    // ever-leaf-personalizer.js:2912, :1745-1746) - a real ancestor boundary
    // that moving those two specific nodes OUT of (into `stage`, a sibling
    // subtree) BEFORE that scoped query runs would break (confirmed: the
    // element tag orders <ever-leaf-mobile-stage.js script> before
    // <ever-leaf-personalizer.js script> in the rendered HTML, so on first
    // load mobile-stage.js's own top-level code always executes first).
    // Rating/Title/Description live in `.ever-leaf-product__info`, and the
    // Dock is a direct child of `.visual` - neither location is ever queried
    // scoped-to-<ever-leaf-personalizer> by anything, so moving them (and
    // revealing the Dock/Carousel) early is safe by construction, not by
    // assumption.
    function moveRealNodesIn(includeToggleAndHints) {
      var uploadNode = document.querySelector('[data-ever-leaf-mobile-target="photo"]');
      var line1Node = document.querySelector('[data-ever-leaf-mobile-target="line1"]');
      var line2Node = document.querySelector('[data-ever-leaf-mobile-target="line2"]');
      var materialNode = document.querySelector('[data-ever-leaf-mobile-target="material"]');
      var ratingNode = document.querySelector('[data-ever-leaf-mobile-target="rating"]');
      var titleNode = document.querySelector('[data-ever-leaf-mobile-target="title"]');
      var priceNode = document.querySelector('[data-ever-leaf-mobile-target="price"]');
      var descriptionNode = document.querySelector('[data-ever-leaf-mobile-target="description"]');
      var headingNode = document.querySelector('[data-ever-leaf-mobile-target="heading"]');
      var infoBackdropNode = document.querySelector('[data-ever-leaf-mobile-target="info-backdrop"]');
      var atcNode = document.querySelector('[data-ever-leaf-mobile-target="atc"]');

      // Top-anchored group (Mobile Y from Top, all independent): Rating,
      // Title, Price, Description, Personalizer Heading, Interaction Hints,
      // View Toggle. Bottom-anchored group (Mobile Y from Bottom): the
      // Configuration Carousel pages below, and Add to Cart (inside the
      // reparented <product-form>, positioned via CSS absolute).
      if (ratingNode) moveTo(ratingNode, stage);
      if (titleNode) moveTo(titleNode, stage);
      if (descriptionNode) moveTo(descriptionNode, stage);
      if (headingNode) moveTo(headingNode, slots.heading);
      if (priceNode) moveTo(priceNode, slots.price);
      // Info Backdrop moves in the SAME early batch as Rating/Title/
      // Description (not queried by any <ever-leaf-personalizer>-scoped
      // code, so no race). Once here, it needs nothing further from JS -
      // its geometry is pure CSS, anchored to the same View-Frame custom
      // properties the Frame itself uses (assets/ever-leaf-product.css).
      if (infoBackdropNode) moveTo(infoBackdropNode, stage);
      if (includeToggleAndHints) {
        var hintsNode = document.querySelector('[data-ever-leaf-mobile-target="hints"]');
        var toggleNode = document.querySelector('[data-ever-leaf-mobile-target="toggle"]');
        if (hintsNode) moveTo(hintsNode, stage);
        if (toggleNode) moveTo(toggleNode, stage);
      }

      // Decorative Separator — reuses the SAME real separator block(s), never
      // duplicated. Both Product Section separator instances share this one
      // Mobile target; if a merchant enables more than one, each still moves
      // (existing per-block Show on Mobile visibility decides which one, if
      // any, is actually visible - see .ever-leaf-block--hide-mobile).
      if (slots.separator) {
        document.querySelectorAll('[data-ever-leaf-mobile-target="separator"]').forEach(function (node) {
          moveTo(node, slots.separator);
        });
      }

      if (pages.photo && uploadNode) moveTo(uploadNode, pages.photo);
      if (pages.line1 && line1Node) moveTo(line1Node, pages.line1);
      if (pages.line2 && line2Node) moveTo(line2Node, pages.line2);
      if (pages.material && materialNode) moveTo(materialNode, pages.material);

      // The real <product-form> (never a clone, ATC stays inside its own
      // <form> so the commit engine and Dawn's product-form.js keep working)
      // moves into the Dock's cta slot; CSS flattens it (display:contents) so
      // the ATC row sits between the Carousel and the Price. Terms / Buy Now
      // (same form) hang just below the Dock; observeBelowDock() reserves
      // their height below the Stage.
      if (atcNode) {
        var productForm = atcNode.closest('product-form');
        if (productForm) moveTo(productForm, slots.cta);
      }

      carousel.hidden = false;
      revealDock();
      observeBelowDock();
      observeCarouselHeight();
    }

    // DOCK BLUR FIRST-VISIBLE-FRAME FIX — the Dock's backdrop-filter blurs
    // whatever paints directly behind it, which on the Mobile Stage is the
    // active Gallery main image (assets/ever-leaf-product.js sets
    // `.is-active` on exactly one `.ever-leaf-product-gallery__main-image`).
    // `dock.hidden = false` used to fire unconditionally here, the instant
    // JS init reached this line - completely decoupled from whether that
    // image had actually finished loading. On a fast/cached image (every
    // DEV re-test in this repo, since the proxy always warms the cache)
    // the two happen to finish in the same order and the bug never shows.
    // On a genuinely cold/slow load the race can flip: the Dock's already-
    // correct `backdrop-filter: blur(8px)` then has nothing but blank
    // background to blur, so the panel reads as flat/unblurred until the
    // image finally paints in - Failure Class G, not a CSS or compositing
    // bug. Confirmed via CDP network throttling: dock stayed `hidden` for
    // ~5s while the image loaded, then revealed ~1s after the image's own
    // `complete` flipped true, with `backdrop-filter` reading `blur(8px)`
    // correctly at every sampled point - the VALUE was never late, only
    // the underlying content was.
    //
    // Fix: reveal immediately when the active image is already loaded
    // (the common case - zero timing change from before), otherwise wait
    // for that image's own `load`/`error` event (never an arbitrary
    // timer) before flipping `hidden`. `error` is included so a genuinely
    // broken image can't hold the Dock hidden forever.
    var dockPendingReveal = false;
    function revealDock() {
      var img = document.querySelector('.ever-leaf-product-gallery__main-image.is-active');
      if (!img || img.complete) {
        dock.hidden = false;
        return;
      }
      dockPendingReveal = true;
      var onReady = function () {
        img.removeEventListener('load', onReady);
        img.removeEventListener('error', onReady);
        if (!dockPendingReveal || !root.hasAttribute('data-ever-leaf-mobile-stage-active')) return;
        dockPendingReveal = false;
        dock.hidden = false;
        // The generic runState() loop already ran once and skipped 'dock'
        // (see the guard in runState() itself) because it was still
        // hidden - give it its real Initial Appearance pass now that
        // there is an actual rect to animate from/to.
        runState(lastHasPhoto, true, ['dock']);
      };
      img.addEventListener('load', onReady, { once: true });
      img.addEventListener('error', onReady, { once: true });
    }

    var belowRO = null;
    var belowMO = null;
    /* ---- Dock tail height: ONE owner, measured geometrically ------------
       The Dock is bottom-anchored with `bottom: Y*scale - var(--ever-leaf-
       dock-tail-h)`, so for the Purchase Row's own bottom edge to land
       exactly Y*scale above the Stage bottom, --ever-leaf-dock-tail-h must
       equal EXACTLY the rendered height of everything below the Purchase
       Row (that is the literal term the formula cancels).

       The previous implementation approximated that term by summing a
       hardcoded list of three selectors (Terms / Buy Now / .rtl-sense-
       banner). That enumeration is the real, remaining reason payment
       icons could still lift the Dock after Pass 8: anything below the
       Purchase Row that is NOT one of those three selectors was never
       subtracted, so the Dock's box grew while its compensating `bottom`
       offset did not - the CSS tail contract itself already lists a fourth
       element (.image-upload-field-container, the upload app's own
       wrapper, deliberately left visible so the DEV reveal toggle keeps
       working), the Purchase Row's own bottom margin (--ever-leaf-dock-gap-
       payment) was likewise never counted, and a third-party app embed can
       always introduce a wrapper element matching none of the three names.

       Measuring the geometry directly removes the enumeration entirely:
       dock bottom minus Purchase Row bottom IS the tail, whatever it
       happens to contain. It is self-correcting for elements this file has
       never heard of, and it cannot drift out of sync with the CSS. No
       circularity: --ever-leaf-dock-tail-h only translates the Dock, and a
       translation moves both measured edges by the same amount, leaving
       their difference (and therefore the next measurement) unchanged. */
    function purchaseRowBottom() {
      // FINAL PRE-STEP-2 PRODUCT CORRECTIVE PASS, Requirement #7 — this
      // used to read only the border-box bottom (excluding ATC's own
      // margin-bottom), on the theory that the margin belonged to a
      // separate "Purchase Row -> Payment" gap setting that would reserve
      // room below the row. That other setting (dock_gap_row_payment) has
      // no CSS consumer at all (confirmed dead) - the ONLY thing that
      // actually sets this margin today is ATC's own "Spacing below
      // (Mobile)" (--ever-leaf-atc-spacing-bottom-mobile -> margin-block-
      // end on .ever-leaf-purchase-sub--atc). Because updateBelowDock()'s
      // tail/`bottom:` formula is a closed feedback loop that keeps
      // whatever this function returns pinned to a fixed screen position,
      // treating that margin as "not part of the row" made it algebraically
      // cancel out - growing it only grew the Dock's own tail/outer edge,
      // never visibly moving ATC. Including the margin here instead makes
      // it part of what the formula protects, so growing it now moves the
      // protected point - and therefore ATC itself - up, creating a real,
      // measurable gap below the button. Add to Cart is `align-self:
      // stretch` so it governs the row height; Price is read too in case
      // Add to Cart is hidden on Mobile by its own merchant setting (Price
      // has no bottom-margin setting of its own, so this is a no-op for it).
      var out = null;
      ['.ever-leaf-purchase-sub--atc', '[data-ever-leaf-dock-slot="price"]'].forEach(function (sel) {
        var n = dock.querySelector(sel);
        if (!n) return;
        var cs = window.getComputedStyle(n);
        if (cs.display === 'none') return;
        var b = n.getBoundingClientRect().bottom + (parseFloat(cs.marginBottom) || 0);
        if (out === null || b > out) out = b;
      });
      return out;
    }
    function updateBelowDock() {
      // MOBILE PERFORMANCE PASS — the dock/stage rects below are read ONCE,
      // together, before ANY style write in this function. The Interaction
      // Hints dock-top read (added in a prior pass) originally re-read both
      // rects AFTER two intervening `style.setProperty()` writes, forcing a
      // second synchronous layout flush on every single call — exactly the
      // "read → write → read" layout-thrash shape the project's own prior
      // performance passes (see trackDockClip()'s frameElRef()/surfaceElFromFrame()
      // split below) already proved is the dominant cost class in this file,
      // now reintroduced here. dockRect.top/stageRect.top are recomputed from
      // the SAME single reflow this function already pays for via
      // dockRect.bottom (used by the tail calc immediately below) — batching
      // them removes the second reflow entirely. This one-cycle-behind
      // snapshot (pre-write geometry) is the same convention purchaseRowBottom()
      // already relies on for the tail calc itself, so this introduces no new
      // staleness class, only removes a redundant layout read.
      var dockRect = dock.getBoundingClientRect();
      var stageRect = stage.getBoundingClientRect();
      var rowBottom = purchaseRowBottom();
      var tail = rowBottom === null ? 0 : Math.max(0, dockRect.bottom - rowBottom);
      dock.style.setProperty('--ever-leaf-dock-tail-h', tail + 'px');
      // dock bottom = Y*scale - tail; anything below the Stage's own box must be reserved.
      // This read is intentionally AFTER the write above (it must reflect the
      // just-written tail-h to compute the correct reserved space) - the one
      // forced-layout pair this function cannot avoid.
      var bottom = parseFloat(window.getComputedStyle(dock).bottom) || 0;
      stage.style.setProperty('--ever-leaf-below-dock', Math.max(0, -bottom) + 'px');
      // PRODUCT SECTION CORRECTIVE PASS, Requirement #4 — Interaction Hints'
      // "Top of Mobile Personalizer Dock" anchor needs the Dock's real, live
      // top edge relative to the Stage (the Dock is bottom-anchored, so this
      // is not derivable from any published reference-canvas var the way the
      // Info Backdrop anchor is). Reuses this SAME ResizeObserver/
      // MutationObserver pair — already the sole owner of Dock geometry
      // measurement — rather than adding a second, competing watcher.
      var dockTop = dockRect.top - stageRect.top;
      stage.style.setProperty('--ever-leaf-hints-dock-top-raw', dockTop + 'px');
    }
    function observeBelowDock() {
      // Exactly TWO watchers, both on the Dock itself - never a per-tail-
      // node registry (which is what could go stale when a third-party app
      // embed, e.g. the Sense RTL payment banner or the upload app,
      // replaces its own node asynchronously after this first pass). The
      // ResizeObserver catches any tail content growing or shrinking (that
      // always changes the Dock's own box height, since tail rows are part
      // of the same grid); the MutationObserver catches a node being
      // inserted or swapped without a size change. The Dock element itself
      // is never replaced, only its children, so both bindings stay valid
      // for the lifetime of this activation.
      if (typeof ResizeObserver === 'function' && !belowRO) {
        belowRO = new ResizeObserver(updateBelowDock);
        belowRO.observe(dock);
      }
      if (typeof MutationObserver === 'function' && !belowMO) {
        belowMO = new MutationObserver(updateBelowDock);
        belowMO.observe(dock, { childList: true, subtree: true });
      }
      updateBelowDock();
    }

    // INFO BACKDROP — STATIC DOCK-LIKE PANEL pass: this is no longer a
    // JS-measured element at all. It is a fixed background panel, pure
    // CSS-positioned exactly like the View Frame itself
    // (.ever-leaf-product__visual-inner: inset-block-start/inset-inline as
    // calc(var(--ever-leaf-frame-*) * var(--ever-leaf-layout-scale)), see
    // assets/ever-leaf-product.css) - same containing block (the Stage),
    // same inherited custom properties, so it can never land in a
    // different coordinate space than the Frame it is anchored to.
    // Rating/Title/Description are no longer geometry inputs of any kind:
    // no union, no ResizeObserver, no getBoundingClientRect() on them at
    // all. It also no longer rides their transitions (see stateTargets()
    // below) - once reparented into the Stage by moveRealNodesIn(), its
    // CSS handles everything and nothing here needs to touch it again.

    /* ---- State transition engine (Mobile Stage only) -------------------
       Rating/Title (No Image) <-> View Toggle/Hints/Visualizer Controls
       (Uploaded Image) slide in from BEHIND the Immersive View Frame edge:
       the element's translate and a clip-path inset are interpolated by the
       same Web Animation (same duration/easing, both linear in progress), so
       the visible part is always exactly the part inside the frame edge -
       translation dominates, nothing fades. The settled states are pure CSS
       (see ever-leaf-product.css); an exiting element is held visible inline
       only while it travels. Timing per element: First Appearance Delay is
       used for the first entrance INSTEAD of Pre-Appear (never stacked);
       Pre-Disappear applies to exits. prefers-reduced-motion bypasses all
       motion and delays. */
    var cfg = null;
    function parseCfg() {
      var raw = stage.getAttribute('data-ever-leaf-state-config') || '';
      var out = { dur: 400 };
      raw.split(';').forEach(function (part) {
        var i = part.indexOf(':');
        if (i < 0) return;
        var k = part.slice(0, i);
        var v = part.slice(i + 1).split(',');
        if (k === 'dur') out.dur = parseFloat(v[0]) || 400;
        // Price carries no motion of its own - it is rendered inside the Dock
        // and travels with it - so it contributes only a participation switch.
        else if (k === 'price') out.priceOn = v[0] !== 'false';
        else if (k === 'desc') out.desc = { dur: parseFloat(v[0]) || 400, up: parseFloat(v[1]) || 0, down: parseFloat(v[2]) || 0, on: v[3] !== 'false' };
        // 'bottom' added for the Mobile Personalizer Dock (Pass 13). Without
        // it this parser silently coerced every non-'side' direction to
        // 'top', which made the Dock slide UP out of the frame instead of
        // down - caught by measurement, not assumed.
        // v[4] is the per-element Enable State Transition switch. Absent
        // (an older cached render, or a block the merchant has not
        // re-saved) means ON, so nothing changes for an existing theme.
        else out[k] = { dir: (v[0] === 'side' || v[0] === 'bottom') ? v[0] : 'top', first: parseFloat(v[1]) || 0, inn: parseFloat(v[2]) || 0, out: parseFloat(v[3]) || 0, on: v[4] !== 'false' };
      });
      out.desc = out.desc || { dur: 400, up: 0, down: 0, on: true };
      if (out.priceOn === undefined) out.priceOn = true;
      return out;
    }
    var seenKeys = {};
    // Per-key settled-state cache (Mobile Personalizer Corrections Pass 3,
    // Item 8) — runState() is now invoked on BOTH a real hasPhoto transition
    // and a pure viewMode (Close-Up<->Full Lamp) change; without this, every
    // invocation unconditionally replayed each key's enter/exit animation
    // from its *computed* shownNow value, even for keys whose shownNow never
    // actually changed (e.g. Rating/Title/Toggle on a viewMode-only call,
    // which only Hints is meant to react to). Gating on "did shownNow change
    // since the last runState() call" lets a key stay silent when nothing
    // about it changed, while still correctly reacting the moment its own
    // input (hasPhoto for most keys, hasPhoto+viewMode for Hints) does.
    var lastShown = {};
    var lastHasPhoto = root.classList.contains('ever-leaf-has-photo');
    var lastDescHasPhoto = lastHasPhoto;
    var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
    var SHADOW_PAD = 20;

    // MOTION ENGINE — ONE ACTIVE TRANSITION OWNER PER TARGET. Rapid
    // retargeting (View Toggle clicked again before the previous transition
    // finished) previously let a STALE animation's own onfinish/oncancel
    // handler run after a NEWER animation had already started for the same
    // element - e.g. an interrupted EXIT's oncancel still unconditionally
    // clearing inline `visibility` right after a NEW entrance had just set
    // it, which could leave a just-shown element governed by whatever the
    // settled CSS said (sometimes hidden) instead of the animation actually
    // running. A per-element monotonically-increasing generation number,
    // captured by each playEdge()/settleEdge() call and re-checked before
    // either handler's cleanup runs, makes only the LATEST call for a given
    // element allowed to apply its cleanup - every superseded one becomes a
    // safe no-op instead of clobbering the current state.
    var elGenerations = new WeakMap();
    function nextGeneration(el) {
      var g = (elGenerations.get(el) || 0) + 1;
      elGenerations.set(el, g);
      return g;
    }
    function isStaleGeneration(el, myGeneration) {
      return elGenerations.get(el) !== myGeneration;
    }

    function reducedMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }
    function isRtl() {
      return (root.closest('[dir]') || document.documentElement).getAttribute('dir') === 'rtl';
    }
    // FINAL INTERACTIVE PERFORMANCE PASS — element resolution split out of
    // the rect read, so a per-frame caller (trackDockClip() below) can
    // resolve the target element ONCE per transition instead of re-running
    // this querySelector on every animation frame. frameRect() itself keeps
    // its exact previous behavior (query + rect, every call) for every
    // other, non-per-frame caller (frameEdges()/edgeFrames(), each called
    // once per transition start, unaffected by this pass).
    function frameElRef() {
      return stage.querySelector('[data-ever-leaf-product-visual]');
    }
    function frameRectFromEl(f) {
      return (f || stage).getBoundingClientRect();
    }
    function frameRect() {
      return frameRectFromEl(frameElRef());
    }
    /* ---- VISIBLE View Frame boundary (Pass 14) -------------------------
       frameRect() above is the View Frame's BOX. That box paints nothing
       and deliberately does not clip (the Visualizer's control row lives in
       the same subtree and has to stay visible below it). The only boundary
       a customer can actually see is the painted visual surface - the
       Visualizer work area, or the Gallery - a centred square that clips
       ITSELF back to the frame and is routinely SHORTER than the frame box.

       Measured on the merchant's own DEV settings at 390x844, by hit-testing
       with elementFromPoint (which honours clip-path) rather than reading
       nominal coordinates: the frame box bottom is 844 (the viewport edge),
       while the visible boundary is 790.6 in Close-Up and 689 in Full Lamp.
       Clipping a participant at the frame box bottom therefore cuts it along
       an invisible line up to 155px below anything that is drawn - which is
       exactly what the Dock's exit did.

       The surface's clipped rect is exactly frameBox n surfaceBox, because
       the surface is centred on the frame's centre and its clip insets are
       per-axis max(0, (S - dim) / 2). So each visible edge is
           centre +/- min(S, frameExtent) / 2
       where S is the square's side. S is linear in --ever-leaf-view-t, so
       two measurements (t=0 and t=1) describe every intermediate frame
       exactly - and keeping min() in the formula preserves the kink where
       the square stops being the limiting factor, instead of interpolating
       straight through it. */
    // FINAL INTERACTIVE PERFORMANCE PASS — same split as frameElRef()/
    // frameRect() above: surfaceElFromFrame() takes an already-resolved
    // frame element so a per-frame caller isn't re-running querySelector +
    // querySelectorAll + a getClientRects() loop on every animation frame.
    // surfaceEl() itself is unchanged for every other, non-per-frame caller.
    function surfaceElFromFrame(f) {
      if (!f) return null;
      var list = f.querySelectorAll('.ever-leaf-visualizer-work-area, .ever-leaf-product-gallery');
      for (var i = 0; i < list.length; i++) {
        if (list[i].getClientRects().length) return list[i];
      }
      return null;
    }
    function surfaceEl() {
      return surfaceElFromFrame(frameElRef());
    }
    // Side of the surface square at a given view-t, read from the REAL
    // stylesheet instead of reimplementing its formula here: override
    // --ever-leaf-view-t on the surface itself (the closest owner -
    // ever-leaf-personalizer.js publishes it further up the tree, so an
    // inline value here is the one that wins), measure, restore. Both
    // happen inside one task, so nothing can paint in between.
    function surfaceSideAt(s, t) {
      var prev = s.style.getPropertyValue('--ever-leaf-view-t');
      s.style.setProperty('--ever-leaf-view-t', String(t));
      var h = s.getBoundingClientRect().height;
      if (prev === '') s.style.removeProperty('--ever-leaf-view-t');
      else s.style.setProperty('--ever-leaf-view-t', prev);
      return h;
    }
    function frameEdges() {
      var s = surfaceEl();
      if (!s) return null;
      var f = frameRect();
      var s0 = surfaceSideAt(s, 0);
      var s1 = surfaceSideAt(s, 1);
      if (!(s0 > 0) || !(s1 > 0)) return null;
      var cy = (f.top + f.bottom) / 2;
      var cx = (f.left + f.right) / 2;
      var fh = f.bottom - f.top;
      var fw = f.right - f.left;
      var side = function (t) { return s0 + (s1 - s0) * t; };
      return {
        bottomAt: function (t) { return cy + Math.min(side(t), fh) / 2; },
        leftAt: function (t) { return cx - Math.min(side(t), fw) / 2; },
        rightAt: function (t) { return cx + Math.min(side(t), fw) / 2; }
      };
    }
    // The visible frame RIGHT NOW, mid-resize included: the surface is being
    // resized live by CSS, so its own rect is the truth and needs no
    // reimplementation of the transition's clock.
    // FINAL INTERACTIVE PERFORMANCE PASS — the actual rect math, taking
    // already-resolved elements so trackDockClip() below can call this
    // directly every frame with cached refs instead of visibleFrameNow()
    // re-querying the DOM tree each time. Defensive by construction, not by
    // an added check: a disconnected/stale surfaceElRef's own
    // getBoundingClientRect() degrades to a zero rect, so `sr.height > 0`
    // is already false and this returns null exactly like the pre-existing
    // "surface not found" path already did — the caller's existing
    // `if (!v || now > dockClipStop)` bail-out (unchanged) still applies.
    function visibleFrameFromRefs(frameEl, surfaceElRef) {
      if (!surfaceElRef) return null;
      var f = frameRectFromEl(frameEl);
      var sr = surfaceElRef.getBoundingClientRect();
      if (!(sr.height > 0)) return null;
      return {
        top: Math.max(f.top, sr.top), bottom: Math.min(f.bottom, sr.bottom),
        left: Math.max(f.left, sr.left), right: Math.min(f.right, sr.right)
      };
    }
    function visibleFrameNow() {
      var f = frameElRef();
      return visibleFrameFromRefs(f, surfaceElFromFrame(f));
    }
    function frameRadius() {
      var v = getComputedStyle(stage).getPropertyValue('--ever-leaf-frame-radius').trim();
      return v || '14px';
    }
    function stateTargets(key) {
      var one = function (sel) { var n = stage.querySelector(sel); return n ? [{ el: n, side: null }] : []; };
      // STATIC DOCK-LIKE PANEL pass — Info Backdrop no longer rides
      // Rating's (or any) transition. It is permanent background layout,
      // never a transition target, so 'rating' returns only Rating itself
      // again, matching every other plain single-target key below.
      if (key === 'rating') return one('[data-ever-leaf-mobile-target="rating"]');
      if (key === 'title') return one('[data-ever-leaf-mobile-target="title"]');
      // Description Slide In/Out (new, shares the engine like every other
      // participant) is fully independent of the pre-existing Uploaded-Y
      // move handled separately at the end of runState() - same element,
      // two orthogonal motions (this one only ever runs on Initial
      // Appearance/View Toggle, the Y-move only ever runs on a hasPhoto
      // change), never both at once in practice since Initial Appearance
      // fires once before any hasPhoto change exists to react to.
      if (key === 'description') return one('[data-ever-leaf-mobile-target="description"]');
      if (key === 'toggle') {
        // FINAL PRE-STEP-2 PRODUCT CORRECTIVE PASS, Requirement #3/#27/#28
        // — the View Toggle's own new physical Side setting (rendered as
        // data-ever-leaf-view-toggle-side on this exact node) is now
        // authoritative for horizontal slide direction whenever the
        // Animation block's own state_dir_toggle is 'side'; this replaces
        // edgeFrames()'s previous isRtl()-only inference for that option
        // (mirrors the SAME pattern already used for controls-movement/
        // controls-zoom above). state_dir_toggle's 'top' option is a
        // completely separate, untouched vertical axis - Side is simply
        // not consulted for it, so there is no competing authority there.
        var toggleNode = stage.querySelector('[data-ever-leaf-mobile-target="toggle"]');
        if (!toggleNode) return [];
        var toggleSide = toggleNode.getAttribute('data-ever-leaf-view-toggle-side') === 'left' ? 'left' : 'right';
        return [{ el: toggleNode, side: toggleSide }];
      }
      if (key === 'controls') return one('.ever-leaf-product__visual-inner [class*="ever-leaf-visualizer-control-panel--horizontal"]');
      // VISUALIZER CONTROLS LAYOUT REFACTOR, Feature C — Mobile VERTICAL
      // layout only (guarded by the same data-ever-leaf-mobile-layout
      // attribute the CSS positioning rules key off, in ever-leaf-product.
      // css). Grid mode renders the identical __movement/__zoom-reset
      // classes but is deliberately NOT matched here (the attribute value
      // would be 'grid', not 'vertical'), so this can never introduce a
      // new slide animation on the pre-existing Grid layout - only the new
      // Vertical layout gets one, mirroring how 'controls' above already
      // only ever targets the Horizontal panel, never Grid.
      if (key === 'controls-movement' || key === 'controls-zoom') {
        var vertPersonalizer = stage.querySelector('ever-leaf-personalizer[data-ever-leaf-mobile-layout="vertical"]');
        if (!vertPersonalizer) return [];
        var vertCls = key === 'controls-movement' ? 'ever-leaf-visualizer-control-panel__movement' : 'ever-leaf-visualizer-control-panel__zoom-reset';
        // CONTROLS FINAL ARCHITECTURE PASS (Section 26) — the Slide In/Out
        // side is DERIVED from the same anchor attribute the CSS position
        // rule reads (assets/ever-leaf-product.css), never a second,
        // independently-settable side. No Auto/Center/logical value exists
        // for this attribute (schema is a strict left/right select), so
        // any other value defensively falls back to left/right per group.
        var vertAnchorAttr = key === 'controls-movement' ? 'data-ever-leaf-controls-movement-anchor' : 'data-ever-leaf-controls-zoom-anchor';
        var vertSide = vertPersonalizer.getAttribute(vertAnchorAttr) === 'right' ? 'right' : (vertPersonalizer.getAttribute(vertAnchorAttr) === 'left' ? 'left' : (key === 'controls-movement' ? 'left' : 'right'));
        var vertNode = vertPersonalizer.querySelector('.' + vertCls);
        return vertNode ? [{ el: vertNode, side: vertSide }] : [];
      }
      if (key === 'dock') return one('[data-ever-leaf-mobile-dock]');
      var out = [];
      stage.querySelectorAll('[data-ever-leaf-mobile-target="hints"] .ever-leaf-hint').forEach(function (h) {
        out.push({ el: h, side: h.classList.contains('ever-leaf-hint--zoom') ? 'right' : 'left' });
      });
      return out;
    }
    // hidden-edge keyframes for one element: [hidden, resting]
    function edgeFrames(el, dir, physSide, timing) {
      el.getAnimations().forEach(function (a) { a.cancel(); });
      var r = el.getBoundingClientRect();
      var f = frameRect();
      var p = SHADOW_PAD;
      // Interaction Hints radius fix (Pass 5, Item 4) - inset() without a
      // `round` argument always produces hard right-angle corners, which
      // during the slide overlay the bubble's own rounded border-radius
      // and visibly square off whichever edge the clip line crosses. Read
      // the element's own actual rendered radius so the animated clip
      // matches its real shape at every frame, not just once settled.
      var radius = getComputedStyle(el).borderRadius || '0px';
      var rnd = ' round ' + radius;
      if (dir === 'top') {
        var d = r.bottom - f.top;
        if (d <= 0) d = r.height;
        var ct = f.top - r.top;
        return [
          { translate: '0px ' + (-d) + 'px', clipPath: 'inset(' + (ct + d) + 'px ' + (-p) + 'px ' + (-p) + 'px ' + (-p) + 'px' + rnd + ')' },
          { translate: '0px 0px', clipPath: 'inset(' + Math.min(ct, -p) + 'px ' + (-p) + 'px ' + (-p) + 'px ' + (-p) + 'px' + rnd + ')' }
        ];
      }
      if (dir === 'bottom') {
        // Same self-clipping technique every other participant uses - no
        // ancestor overflow, no extra clipping layer - but pinned to the
        // VISIBLE frame edge (see frameEdges() above) instead of the frame
        // BOX. Pass 13 used the box, which on the merchant's settings sits
        // at the viewport edge, so the Dock slid across up to 155px of bare
        // stage and was then cut by nothing the customer could see. Pass
        // 13's harness could not catch this because it faked the frame as a
        // plain element WITH a background, so its box and its visible
        // boundary were the same rectangle; production paints neither.
        var fe = frameEdges();
        // Travel far enough that the Dock's top clears the edge at its
        // LOWEST (the Full Lamp edge); the live tracker below owns the cut
        // itself, so this only has to be far enough, never exact.
        var db = (fe ? fe.bottomAt(1) : f.bottom) - r.top;
        if (db <= 0) db = r.height;
        // Deliberately NO clipPath in these keyframes. The visible edge
        // moves on the VIEW TRANSITION's own clock (ever-leaf-personalizer.js
        // eases --ever-leaf-view-t with easeInOutCubic over its own merchant
        // duration - 850ms on DEV) while this animation runs on the shared
        // state Duration (400ms) with a different easing. Two interpolated
        // keyframes therefore cannot stay on a line that is moving to a
        // foreign clock: measured, that left the Dock painted up to 66px
        // past the edge on the way back in. trackDockClip() writes the cut
        // from live geometry every frame instead, which is exact by
        // construction for any combination of durations and easings.
        return [
          { translate: '0px ' + db + 'px' },
          { translate: '0px 0px' }
        ];
      }
      var side = physSide || (isRtl() ? 'right' : 'left');
      if (side === 'left') {
        var dl = r.right - f.left;
        if (dl <= 0) dl = r.width;
        var cl = f.left - r.left;
        return [
          { translate: (-dl) + 'px 0px', clipPath: 'inset(' + (-p) + 'px ' + (-p) + 'px ' + (-p) + 'px ' + (cl + dl) + 'px' + rnd + ')' },
          { translate: '0px 0px', clipPath: 'inset(' + (-p) + 'px ' + (-p) + 'px ' + (-p) + 'px ' + Math.min(cl, -p) + 'px' + rnd + ')' }
        ];
      }
      var dr = f.right - r.left;
      if (dr <= 0) dr = r.width;
      var cr = r.right - f.right;
      return [
        { translate: dr + 'px 0px', clipPath: 'inset(' + (-p) + 'px ' + (cr + dr) + 'px ' + (-p) + 'px ' + (-p) + 'px' + rnd + ')' },
        { translate: '0px 0px', clipPath: 'inset(' + (-p) + 'px ' + Math.min(cr, -p) + 'px ' + (-p) + 'px ' + (-p) + 'px' + rnd + ')' }
      ];
    }
    /* Live View Frame cut for the Dock (Pass 14).

       The Dock's `translate` stays in the one WAAPI animation with every
       other participant; only the CLIP is driven here. Each frame it reads
       the Dock's real rect - which already includes the animated translate -
       and the frame's real visible edge, and writes the exact inset. That
       removes the clock coupling entirely: it is correct whatever the
       merchant sets the state Duration and the View Transition Duration to,
       and whatever easings the two use.

       It keeps running after the Dock's own animation has finished, until
       the Dock is genuinely inside the visible frame again, because on the
       way back in the frame is still growing for another ~450ms on DEV's
       settings - stopping at the animation's end would strand the Dock's
       last ~23px over bare stage, or snap the clip off. */
    var dockClipRaf = null;
    var dockClipStop = 0;
    function clearDockClip(el) {
      if (dockClipRaf !== null) { cancelAnimationFrame(dockClipRaf); dockClipRaf = null; }
      if (el) el.style.removeProperty('clip-path');
    }
    function trackDockClip(el, anim) {
      clearDockClip(el);
      var rndF = ' round ' + frameRadius();
      // Safety stop, so a transition that never settles (a cancelled
      // animation, a torn-down section) cannot leave a loop running.
      dockClipStop = (window.performance && performance.now ? performance.now() : Date.now()) + 4000;
      // FINAL INTERACTIVE PERFORMANCE PASS — resolve the frame/surface
      // elements ONCE per transition instead of on every animation frame.
      // Previously each frame() tick called visibleFrameNow(), which re-ran
      // 2 querySelector/querySelectorAll DOM searches (plus a
      // getClientRects() loop) from scratch — real, measurable work
      // repeated for the full ~450-850ms a Dock transition can run, on
      // every single View Toggle click. The elements' IDENTITY cannot
      // change mid-transition (Gallery<->Visualizer switching is a
      // separate, non-concurrent user action from the Dock's own View-
      // Toggle-driven slide), so caching the refs changes nothing about
      // the GEOMETRY read every frame (still a fresh getBoundingClientRect()
      // each tick, exactly as before) or the clip-path values computed —
      // only the redundant re-querying is removed. Safe by construction if
      // the cached surface ever did go stale: visibleFrameFromRefs()
      // degrades to null exactly like "surface not found" already did,
      // hitting the existing `if (!v || now > dockClipStop)` bail-out below
      // — no new failure mode introduced.
      var frameEl = frameElRef();
      var surfaceElRef = surfaceElFromFrame(frameEl);
      function frame() {
        dockClipRaf = null;
        if (!el.isConnected) { clearDockClip(null); return; }
        var v = visibleFrameFromRefs(frameEl, surfaceElRef);
        var now = (window.performance && performance.now ? performance.now() : Date.now());
        if (!v || now > dockClipStop) { clearDockClip(el); return; }
        var r = el.getBoundingClientRect();
        var edge = v.bottom;
        var running = !!anim && anim.playState === 'running';
        // Nothing left to cut once the Dock has stopped moving and is either
        // back inside the visible frame or has been handed over to the CSS
        // settled-hidden state: release the clip so the settled element is
        // exactly its normal self.
        if (!running && (r.bottom <= edge || getComputedStyle(el).visibility === 'hidden')) {
          clearDockClip(el);
          return;
        }
        // The frame owns the cutting edge, so the cut carries the FRAME's
        // radius - not the Dock's own, which is 0px and sliced a hard square
        // line across a visibly rounded frame. The side insets are aligned
        // to the frame too, so those corners are the frame's own; where the
        // Dock is narrower than the frame they go negative, the curve falls
        // outside it and the cut stays straight - correct, since a Dock that
        // never reaches the frame's corners is not cut by them.
        el.style.setProperty(
          'clip-path',
          'inset(' + (-SHADOW_PAD) + 'px ' + (r.right - v.right) + 'px ' +
            Math.max(r.bottom - edge, -SHADOW_PAD) + 'px ' + (v.left - r.left) + 'px' + rndF + ')'
        );
        dockClipRaf = requestAnimationFrame(frame);
      }
      frame();
    }
    /* Normalise an element back to its canonical resting state, without
       animating the recovery. Used when a transition is switched OFF and when
       the switches are resynchronised, so a participant can never be stranded
       mid-flight: it cancels any running or filling WAAPI animation and drops
       every transient value a transition can leave behind - the translate, the
       clip, the inline `visibility` an exiting element is held with, the
       pointer-events lock, and the Dock's live frame clip. What the element
       then LOOKS like is CSS-owned, which is why the
       [data-ever-leaf-state-off~="..."] opt-outs exist alongside this. */
    function settleEdge(el) {
      if (!el) return;
      nextGeneration(el);
      el.getAnimations().forEach(function (a) { a.cancel(); });
      el.style.removeProperty('visibility');
      el.style.removeProperty('translate');
      el.style.removeProperty('clip-path');
      el.style.removeProperty('pointer-events');
      // BUGFIX — clearDockClip(null) here used to run unconditionally for
      // ANY settling element, killing the Dock's live per-frame clip
      // tracker (trackDockClip()) even when a completely different
      // participant (Rating/Title/etc, settled in the SAME runState() pass
      // the Dock also animates in) was the one actually being settled -
      // stopping the RAF loop without clearing the Dock's OWN inline
      // clip-path, leaving it visually frozen mid-cut. Scoped to only
      // actually clear the tracker when the element BEING settled is the
      // Dock itself.
      if (el.hasAttribute && el.hasAttribute('data-ever-leaf-mobile-dock')) clearDockClip(el);
    }
    function playEdge(target, dir, entering, delay, dur) {
      var el = target.el;
      if (!el.isConnected || el.getClientRects().length === 0) return;
      // RETARGETING FIX — capture the element's CURRENT visual translate/
      // clip-path BEFORE edgeFrames() cancels whatever is currently
      // running (cancelling snaps it to its non-animated computed style
      // immediately, in the same synchronous call, before any further
      // measurement). Without this, interrupting a still-moving element
      // (e.g. a second View Toggle click before the first transition
      // finishes) made edgeFrames() measure the element AFTER it had
      // already snapped back to its resting/hidden position, so the new
      // transition started from the WRONG place - the exact "snap back to
      // the nominal starting position" this task called out. Only
      // meaningful when something was actually running; an interrupted-
      // nothing (the normal case) keeps the original two-keyframe path
      // untouched.
      var wasRunning = el.getAnimations().some(function (a) { return a.playState === 'running' || a.playState === 'pending'; });
      var capturedStart = null;
      if (wasRunning) {
        var runningCs = getComputedStyle(el);
        capturedStart = { translate: runningCs.translate, clipPath: runningCs.clipPath };
      }
      var frames = edgeFrames(el, dir, target.side, { delay: delay, dur: dur, entering: entering });
      if (!entering) {
        frames = frames.slice().reverse();
        el.style.visibility = 'visible';
      }
      if (capturedStart) frames = [capturedStart, frames[1]];
      var myGeneration = nextGeneration(el);
      var anim;
      try {
        anim = el.animate(frames, { duration: dur, delay: delay, easing: EASE, fill: entering ? 'backwards' : 'both' });
      } catch (e) {
        el.style.removeProperty('visibility');
        return;
      }
      // The Dock is cut by the View Frame's real, live edge rather than by
      // interpolated keyframes - see trackDockClip().
      if (dir === 'bottom') trackDockClip(el, anim);
      anim.onfinish = function () {
        if (isStaleGeneration(el, myGeneration)) return;
        if (!entering) el.style.removeProperty('visibility');
        try { anim.cancel(); } catch (e2) { /* already cancelled */ }
      };
      anim.oncancel = function () {
        if (isStaleGeneration(el, myGeneration)) return;
        if (!entering) el.style.removeProperty('visibility');
      };
    }
    /* THE single visibility formula for every state participant - runState()
       and the Enable-State-Transition resync both call this, so there is never
       a second competing copy of it.

       `on === false` (Enable State Transition OFF) removes the VIEW TOGGLE
       factor and NOTHING else: the element is excluded from the View Toggle
       choreography and stays pinned on screen, but every other eligibility
       rule still applies. Concretely - Hints still require a photo, so a
       pinned Hint is not forced on screen when there is no photo to hint
       about; Rating/Title/View Toggle/Visualizer Controls are gated on
       hasPhoto alone and never had a View Toggle factor to remove, so OFF
       only stops them animating; and the Dock's sole condition WAS the View
       Toggle, so switched OFF it is simply always on screen. */
    function stateShown(key, hasPhoto, isCloseUp, on) {
      var shownWhenPhoto = key === 'toggle' || key === 'hints' || key === 'controls' || key === 'controls-movement' || key === 'controls-zoom';
      var shown = shownWhenPhoto ? hasPhoto : !hasPhoto;
      // CONTROLS FINAL ARCHITECTURE PASS (Section 7-18) — Vertical Movement/
      // Zoom reuse Hints' EXACT eligibility formula (same photo source, same
      // isCloseUp source, same State ON/OFF semantics): shown only with a
      // photo AND in Close-Up, or (when the group's own State ON is OFF)
      // hasPhoto alone with no Close-Up restriction, matching Hints'
      // documented State-OFF behavior above. The pre-existing 'controls' key
      // (Horizontal layout) is untouched - it never had this isCloseUp gate
      // and this pass does not add one to it.
      if (key === 'hints' || key === 'controls-movement' || key === 'controls-zoom') return on ? (shown && isCloseUp) : shown;
      // The Mobile Personalizer Dock follows the View Toggle ONLY: it is the
      // customer's whole personalization surface, so it must stay available
      // before a photo exists (that is what the content-lock model is for)
      // and must slide away only when Full Lamp is being shown. Deliberately
      // NOT hasPhoto-gated.
      if (key === 'dock') return on ? isCloseUp : true;
      // Description never hides via this system (with or without a photo,
      // without OR with Channel B on) - it only ever Y-shifts between two
      // on-screen rows, handled entirely separately at the end of
      // runState(). Its Slide In/Out participation here exists solely for
      // a Channel A entrance on first Mobile appearance.
      if (key === 'description') return true;
      return shown;
    }
    // TWO-CHANNEL PARTICIPATION MODEL — Channel A (Initial Appearance) and
    // Channel B (View Toggle Transition) are now independent, reusing the
    // two settings that already existed for this exact purpose rather than
    // adding new ones: Channel A = "First Appearance Delay" (`c.first`,
    // already merchant-facing, already meant "delay before the first
    // entrance"; 0 = no entrance, exactly as before) - now checked on its
    // own, so it no longer requires Channel B's "Enable State Transition"
    // (`c.on`) to also be true. Channel B = `c.on` unchanged in every other
    // respect (still the sole gate for ongoing View-Toggle-driven show/
    // hide). Previously `if (!on) { ...; return; }` ran BEFORE the
    // `initial` branch, so on=false silently blocked Channel A too - the
    // exact coupling that made "Dock Initial Appearance = ON, View Toggle
    // Transition = OFF" unreachable. The fix is this reordering alone: the
    // `initial` branch now runs first and no longer consults `on` at all
    // (stateShown() already folds `on` into `shownNow` correctly for every
    // key, including Dock's "always shown when off" rule - see its own
    // comment - so an element pinned always-visible by Channel B being off
    // can still play a Channel A entrance into that pinned state).
    function runState(hasPhoto, initial, onlyKeys) {
      if (!root.hasAttribute('data-ever-leaf-mobile-stage-active')) return;
      cfg = parseCfg();
      var reduced = reducedMotion();
      // Interaction Hints/View Toggle resync fix (Mobile Dock Correction
      // Pass 2) — Hints are only ever relevant while actually viewing
      // Close-Up (dragging/zooming a Full Lamp view isn't offered), so
      // Hints visibility is hasPhoto AND View Toggle state together -
      // matching assets/ever-leaf-personalizer.js's OWN authoritative
      // computeHintsShouldShow() formula exactly (not a second, competing
      // formula). View Toggle itself and Visualizer Controls stay
      // hasPhoto-only; they're meant to stay put across a Toggle switch.
      var isCloseUp = (window.EverLeafPersonalizerState ? window.EverLeafPersonalizerState.get().viewMode : null) !== 'full_lamp';
      var keys = onlyKeys || ['rating', 'title', 'description', 'toggle', 'hints', 'controls', 'controls-movement', 'controls-zoom', 'dock'];
      keys.forEach(function (key) {
        // The Dock's own structural reveal can be deferred behind its
        // Gallery image loading (see revealDock()) - skip it here while
        // that wait is in flight, since it has no real rect yet to
        // animate from/to. revealDock() re-invokes runState(...,['dock'])
        // the moment it actually reveals, so nothing is lost, only delayed
        // to when there is something real to show.
        if (key === 'dock' && dock.hidden) return;
        var c = cfg[key];
        if (!c) return;
        var on = c.on !== false;
        var shownNow = stateShown(key, hasPhoto, isCloseUp, on);
        var t = stateTargets(key);
        if (initial) {
          lastShown[key] = shownNow;
          // initial render is settled; only an explicit First Appearance Delay animates it
          if (!shownNow) return;
          seenKeys[key] = true;
          if (reduced || c.first <= 0) return;
          t.forEach(function (x) { playEdge(x, c.dir, true, c.first, cfg.dur); });
          return;
        }
        // The no-replay cache (lastShown) and the first-appearance
        // bookkeeping (seenKeys) are still maintained while OFF, so turning it
        // back on resumes cleanly instead of replaying a transition the
        // element never missed. The settled appearance itself is CSS-owned -
        // see the [data-ever-leaf-state-off~="..."] opt-outs in
        // assets/ever-leaf-product.css, which are what actually keep a pinned
        // element on screen.
        if (!on) {
          lastShown[key] = shownNow;
          seenKeys[key] = seenKeys[key] || shownNow;
          t.forEach(function (x) { settleEdge(x.el); });
          return;
        }
        if (shownNow === lastShown[key]) return;
        lastShown[key] = shownNow;
        if (reduced) {
          seenKeys[key] = seenKeys[key] || shownNow;
          return;
        }
        if (shownNow) {
          var delay = seenKeys[key] ? c.inn : c.first;
          seenKeys[key] = true;
          t.forEach(function (x) { playEdge(x, c.dir, true, delay, cfg.dur); });
        } else {
          t.forEach(function (x) { playEdge(x, c.dir, false, c.out, cfg.dur); });
        }
      });
      // Description: pure vertical move between its normal and Uploaded Y —
      // same Item 8 fix: only actually animate when hasPhoto itself changed
      // since the last call, so a viewMode-only runState() invocation can't
      // re-trigger the 0->delta glide on an already-settled Description.
      var desc = stage.querySelector('[data-ever-leaf-mobile-target="description"]');
      if (desc && cfg.desc.on === false && hasPhoto !== lastDescHasPhoto) {
        lastDescHasPhoto = hasPhoto;
        settleEdge(desc);
      } else if (desc && !initial && !reduced && hasPhoto !== lastDescHasPhoto && desc.getClientRects().length) {
        lastDescHasPhoto = hasPhoto;
        var cs = window.getComputedStyle(desc);
        var y = parseFloat(cs.getPropertyValue('--ever-leaf-stage-y-from-top')) || 0;
        var yUp = parseFloat(cs.getPropertyValue('--ever-leaf-stage-y-uploaded'));
        if (!isNaN(yUp)) {
          var scale = parseFloat(window.getComputedStyle(root).getPropertyValue('--ever-leaf-layout-scale')) || 1;
          var delta = (yUp - y) * scale;
          desc.getAnimations().forEach(function (a) { a.cancel(); });
          var da = desc.animate(
            [{ translate: '0px ' + (hasPhoto ? 0 : delta) + 'px' }, { translate: '0px ' + (hasPhoto ? delta : 0) + 'px' }],
            { duration: cfg.desc.dur, delay: hasPhoto ? cfg.desc.up : cfg.desc.down, easing: EASE, fill: 'backwards' }
          );
          da.onfinish = function () { da.cancel(); };
        }
      }
    }
    var stateMO = new MutationObserver(function () {
      var now = root.classList.contains('ever-leaf-has-photo');
      if (now === lastHasPhoto) return;
      lastHasPhoto = now;
      // Same edge-detected observer that already drives the state
      // transitions now also flips the Carousel lock, so a photo being
      // removed re-locks Line 1/Line 2/Color and returns the customer to
      // Upload rather than leaving them on a page they can no longer use.
      // No extra observer or subscription was added for this.
      applyContentLockState();
      runState(now, false);
    });
    stateMO.observe(root, { attributes: true, attributeFilter: ['class'] });

    // Interaction Hints/View Toggle resync fix — View Toggle state lives in
    // EverLeafPersonalizerState (viewMode), not a DOM class/attribute this
    // script would otherwise see, so the Dock's own state engine never knew
    // when a Close-Up<->Full Lamp switch happened and Hints stopped
    // following it. Edge-detected (only re-runs on an actual viewMode
    // change) so unrelated state updates - drag/zoom/dedication/etc, which
    // fire far more often - never redundantly re-enter runState().
    //
    // CSS alone can't read EverLeafPersonalizerState, so the current View
    // Toggle mode is ALSO published as a plain DOM attribute (mirroring how
    // `ever-leaf-has-photo` already does this for uploadStatus) for the
    // settled-visibility rule in ever-leaf-product.css to key off. Only the
    // Hints WRAPPER's visibility uses it - individual hints keep their own
    // fixed resting position, so the WAAPI engine's own slide (an inline
    // `visibility: visible` override on the exiting element, same technique
    // already used for Rating/Title/Toggle) always has a stable rect to
    // animate from, regardless of subscriber firing order.
    function applyViewModeAttr() {
      var mode = window.EverLeafPersonalizerState ? window.EverLeafPersonalizerState.get().viewMode : null;
      root.setAttribute('data-ever-leaf-view-mode', mode || 'close_up');
    }
    var unsubscribeViewMode = null;
    var lastViewMode = null;
    // assets/ever-leaf-personalizer.js sets window.EverLeafPersonalizerState;
    // this SAME file's own initStage() already had to solve this exact
    // same-tick `defer` script ordering race once before (see the View
    // Toggle first-activation comment a few lines down) - same fix here:
    // wait one macrotask so a same-tick sibling `defer` script's own
    // synchronous init runs first, with a short bounded retry as a safety
    // net rather than assuming a single deferral is always enough.
    (function wireViewModeSync(attemptsLeft) {
      if (!window.EverLeafPersonalizerState) {
        if (attemptsLeft > 0) setTimeout(function () { wireViewModeSync(attemptsLeft - 1); }, 0);
        return;
      }
      lastViewMode = window.EverLeafPersonalizerState.get().viewMode;
      unsubscribeViewMode = window.EverLeafPersonalizerState.subscribe(function (s) {
        if (s.viewMode === lastViewMode) return;
        lastViewMode = s.viewMode;
        applyViewModeAttr();
        runState(lastHasPhoto, false);
      });
      if (root.hasAttribute('data-ever-leaf-mobile-stage-active')) {
        applyViewModeAttr();
      }
    })(20);
    /* An Enable State Transition switch has to take effect the moment it is
       saved, without waiting for the customer to work the View Toggle. Saving
       a setting usually re-renders the whole section, but a block-only update
       can leave this same element in place, so the config attribute is watched
       and the switches are re-applied directly.

       Three things are resynchronised, in this order:
         1. `data-ever-leaf-state-off`, because the SETTLED appearance is
            CSS-owned - without this a newly-pinned element would stay hidden
            no matter what the JS did.
         2. Anything now OFF is normalised back to its resting state
            immediately and WITHOUT animating the recovery: any running or
            filling WAAPI animation is cancelled and every transient
            translate/clip/visibility/pointer-events value is dropped.
         3. The no-replay cache is re-pointed at each element's CURRENT
            effective state. That is what stops a switch change from replaying
            a stale edge: turning one back ON re-arms it against where things
            actually stand now, so the NEXT genuine View Toggle change is the
            next thing that animates. */
    function syncStateSwitches() {
      cfg = parseCfg();
      var off = [];
      var isCloseUp = (window.EverLeafPersonalizerState ? window.EverLeafPersonalizerState.get().viewMode : null) !== 'full_lamp';
      ['rating', 'title', 'toggle', 'hints', 'controls', 'controls-movement', 'controls-zoom', 'dock', 'price', 'description'].forEach(function (key) {
        var c = key === 'description' ? cfg.desc : cfg[key];
        // Price carries no motion of its own (it is rendered inside the Dock),
        // so it never appears in the state config - only in the opt-out list.
        if (key === 'price') { if (cfg.priceOn === false) off.push(key); return; }
        if (!c || c.on !== false) return;
        off.push(key);
      });
      var attr = off.join(' ');
      if (stage.getAttribute('data-ever-leaf-state-off') !== attr) {
        stage.setAttribute('data-ever-leaf-state-off', attr);
      }
      ['rating', 'title', 'description', 'toggle', 'hints', 'controls', 'controls-movement', 'controls-zoom', 'dock'].forEach(function (key) {
        var c = cfg[key];
        if (!c) return;
        var on = c.on !== false;
        if (!on) stateTargets(key).forEach(function (x) { settleEdge(x.el); });
        lastShown[key] = stateShown(key, lastHasPhoto, isCloseUp, on);
        seenKeys[key] = seenKeys[key] || lastShown[key];
      });
      if (cfg.desc && cfg.desc.on === false) {
        var d = stage.querySelector('[data-ever-leaf-mobile-target="description"]');
        if (d) settleEdge(d);
      }
      lastDescHasPhoto = lastHasPhoto;
    }
    var stateCfgObserver = new MutationObserver(syncStateSwitches);
    stateCfgObserver.observe(stage, { attributes: true, attributeFilter: ['data-ever-leaf-state-config'] });

    // EARLY_KEYS — the state-transition participants that can safely
    // receive their Initial Appearance pass in the SAME early call that
    // reparents them, since their DOM nodes already exist in `stage` at
    // that point. Toggle/Hints are deliberately excluded: their nodes are
    // not moved until the still-deferred activateMobileToggleHints() runs,
    // so stateTargets('toggle'/'hints') would just resolve empty here -
    // explicit exclusion documents that rather than relying on the empty
    // result being silently correct. controls-movement/controls-zoom are
    // included here (not deferred like toggle/hints): their DOM, like
    // 'controls', is rendered directly by the Visualizer block and never
    // reparented, so it already exists at this same early point.
    var EARLY_KEYS = ['rating', 'title', 'description', 'controls', 'controls-movement', 'controls-zoom', 'dock'];

    function activateMobile(includeToggleAndHints) {
      var isFull = includeToggleAndHints !== false;
      root.setAttribute('data-ever-leaf-mobile-stage-active', 'true');
      applyMobileReferenceGeometry(root);
      moveRealNodesIn(isFull);
      setupObserver();
      applyContentLockState();
      // Restore the page this section was last on (Theme Editor re-render),
      // falling back to Upload whenever that destination is missing or
      // still photo-locked. A real first page load has nothing remembered,
      // so this resolves to 'photo' exactly as before.
      //
      // The scroll must actually happen (instantly): the track itself is a
      // fresh element sitting at scrollLeft 0, so merely setting the active
      // key would leave Upload on screen - and the IntersectionObserver,
      // seeing Upload intersecting, would immediately "correct" the active
      // page back to it. Scrolling also arms the existing scrollTargetKey
      // guard, so no intermediate page can flash as active on the way.
      setActivePage(recallActivePage(), { instant: true });
      lastHasPhoto = root.classList.contains('ever-leaf-has-photo');
      applyViewModeAttr();
      runState(lastHasPhoto, true, isFull ? null : EARLY_KEYS);
    }

    // Completes a first activation that started early/partial (see
    // initStage()'s call site below): moves ONLY the two nodes that had to
    // wait, then gives them their own Initial Appearance pass. Deliberately
    // NOT a second call to activateMobile() - that would re-run
    // setupObserver()/applyContentLockState()/setActivePage() redundantly
    // and, worse, re-run runState(..., true) for every key, replaying
    // Rating/Title's entrance a second time (the `initial` branch does not
    // check seenKeys before deciding to play - see runState()).
    function activateMobileToggleHints() {
      var hintsNode = document.querySelector('[data-ever-leaf-mobile-target="hints"]');
      var toggleNode = document.querySelector('[data-ever-leaf-mobile-target="toggle"]');
      if (hintsNode) moveTo(hintsNode, stage);
      if (toggleNode) moveTo(toggleNode, stage);
      runState(lastHasPhoto, true, ['toggle', 'hints']);
    }

    function deactivateMobile() {
      root.removeAttribute('data-ever-leaf-mobile-stage-active');
      clearMobileReferenceGeometry(root);
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      carousel.hidden = true;
      dock.hidden = true;
      if (belowRO) { belowRO.disconnect(); belowRO = null; }
      if (belowMO) { belowMO.disconnect(); belowMO = null; }
      if (heightRO) { heightRO.disconnect(); heightRO = null; }
      uploadSeen = 0;
      track.style.removeProperty('--ever-leaf-carousel-h');
      stage.style.removeProperty('--ever-leaf-below-dock');
      dock.style.removeProperty('--ever-leaf-dock-tail-h');
      restoreAll();
      movedNodes = [];
      firstSyncPending = false;
      dockPendingReveal = false;
    }

    // Set only by the early/partial first activation below (initStage()'s
    // own call site); every other path (resize, Theme Editor reload) is
    // long after page load and never sets this, so sync() takes the
    // existing full-activation branch exactly as before.
    var firstSyncPending = false;
    function sync() {
      if (mql.matches) {
        if (firstSyncPending) {
          firstSyncPending = false;
          // Guard against the narrow race where a resize back to Desktop
          // (a separate 'change' event on this same mql, deactivating the
          // stage) interleaves before this deferred callback runs - moving
          // Toggle/Hints into an already-deactivated stage would be wrong.
          if (root.hasAttribute('data-ever-leaf-mobile-stage-active')) activateMobileToggleHints();
        } else {
          activateMobile();
        }
      } else {
        deactivateMobile();
      }
    }

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', sync);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(sync); // eslint-disable-line no-restricted-properties -- Safari <14 fallback
    }

    // Theme Editor "lighter-weight in-place refresh" self-heal (Pass 3, Item
    // 6 — real root cause, found via a closer-to-real repro than the prior
    // pass's synthetic test). The OLD version of this observer (removed)
    // was created ONCE at module load and called `.observe()` on whatever
    // `.ever-leaf-product__visual` node object existed at that moment. When
    // Shopify's preview instrumentation does the "reuse root, replace
    // children" refresh this comment block already described (see initStage
    // above) by removing the ENTIRE OLD `.ever-leaf-product__visual` and
    // inserting a BRAND NEW one, that removal/insertion is a childList
    // mutation on `.ever-leaf-product` (the ROOT), not on the now-detached
    // `.ever-leaf-product__visual` the stale observer was still watching -
    // so it silently never fired again for that root, `initStage()` never
    // re-ran, and moveRealNodesIn() never cleared the Dock/Carousel's
    // server-rendered `hidden` attribute on the new subtree: a permanent,
    // silent freeze with zero console/exception signal. Fixed by observing
    // the ROOT itself, scoped per-root inside initStage() (so it always
    // tracks the CURRENT live root reference and self-heals through any
    // number of repeated refreshes) instead of a one-time external scan.
    //
    // The condition ALSO had to change, not just what's observed: checking
    // `!root.querySelector('[data-ever-leaf-mobile-dock]')` (the original
    // logic) tests whether SOME element matching that selector exists
    // ANYWHERE in root - but the freshly-inserted replacement subtree
    // ALWAYS contains a Dock element too (server-rendered `hidden` by
    // default), so that condition was FALSE (found) even for a brand-new,
    // not-yet-bound Dock - confirmed empirically: relocating the observer
    // alone did not fix the reproduced freeze. The correct check is
    // whether THIS closure's own bound `stage`/`dock` node objects (not
    // "a" dock, THIS one) are still connected to the live document -
    // `.isConnected` goes false the instant a bound node is detached by a
    // replacement, which is the actual condition that makes every closure
    // in this initStage() call (moveRealNodesIn, setActivePage, runState,
    // the observers below) stale and non-functional against the new DOM.
    var rebindObserver = null;
    if (window.Shopify && window.Shopify.designMode) {
      rebindObserver = new MutationObserver(function () {
        if (!stage.isConnected || !dock.isConnected) initStage(root);
      });
      rebindObserver.observe(root, { childList: true, subtree: true });
    }

    /* ---- Mobile on-screen keyboard: restore the view after editing ------
       Focusing Line 1 / Line 2 opens the OS keyboard, and the browser
       scrolls/resizes the page to keep the caret visible. That behaviour is
       OS-controlled and deliberately NOT suppressed here. What this does is
       remember the composition the customer was looking at BEFORE the
       keyboard appeared and put it back once the keyboard has genuinely
       closed and the visual viewport has settled.

       It restores the PAGE viewport only - it never touches photoCenterX/Y,
       photo scale, the Close-Up/Full Lamp view mode or any other
       personalization geometry. */
    var kbSession = null;
    var kbBlurTimer = null;
    var kbSettleTimer = null;
    var kbRaf = null;
    var vv = window.visualViewport || null;

    function isDedicationInput(el) {
      return !!(el && el.matches && el.matches('[data-ever-leaf-cal-text-line1], [data-ever-leaf-cal-text-line2]'));
    }
    function viewportHeight() {
      return vv ? vv.height : window.innerHeight;
    }
    function stageViewportTop() {
      return stage.getBoundingClientRect().top;
    }
    function cancelKbTimers() {
      if (kbBlurTimer) { clearTimeout(kbBlurTimer); kbBlurTimer = null; }
      if (kbSettleTimer) { clearTimeout(kbSettleTimer); kbSettleTimer = null; }
      if (kbRaf) { cancelAnimationFrame(kbRaf); kbRaf = null; }
    }
    /* The return glide (Pass 12). Deliberately NOT
       `scrollTo({behavior:'smooth'})`: native smooth scrolling has no
       configurable duration, so the merchant's "Keyboard return duration"
       setting could not be honoured. This is a plain rAF tween over the
       same proven Stage anchor - it changes page scroll only and never
       touches photo geometry, view mode, dedication state or the active
       Carousel page. */
    var kbAnim = null;
    function cancelKbAnimation() {
      if (kbAnim) {
        cancelAnimationFrame(kbAnim.raf);
        kbAnim.stop();
        kbAnim = null;
      }
    }
    function keyboardReturnMs() {
      var raw = parseFloat(dock.getAttribute('data-ever-leaf-keyboard-return-ms'));
      if (isNaN(raw) || raw < 0) raw = 500;
      // A reduced-motion preference always wins over the merchant value.
      return reducedMotion() ? 0 : raw;
    }
    function animateScrollTo(target, duration) {
      cancelKbAnimation();
      var start = window.pageYOffset;
      var delta = target - start;
      if (duration <= 0 || Math.abs(delta) < 1) {
        window.scrollTo(0, Math.max(0, target));
        return;
      }
      var t0 = null;
      // Never fight the customer: any deliberate input during the glide
      // cancels it and leaves the page exactly where they put it.
      var onUserInput = function () { cancelKbAnimation(); };
      var opts = { passive: true };
      window.addEventListener('wheel', onUserInput, opts);
      window.addEventListener('touchstart', onUserInput, opts);
      window.addEventListener('pointerdown', onUserInput, opts);
      window.addEventListener('keydown', onUserInput, opts);
      var stop = function () {
        window.removeEventListener('wheel', onUserInput, opts);
        window.removeEventListener('touchstart', onUserInput, opts);
        window.removeEventListener('pointerdown', onUserInput, opts);
        window.removeEventListener('keydown', onUserInput, opts);
      };
      var step = function (ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min(1, (ts - t0) / duration);
        // easeOutCubic - settles without an abrupt final snap.
        var eased = 1 - Math.pow(1 - p, 3);
        window.scrollTo(0, Math.max(0, start + delta * eased));
        if (p < 1) {
          kbAnim.raf = requestAnimationFrame(step);
        } else {
          stop();
          kbAnim = null;
        }
      };
      kbAnim = { raf: requestAnimationFrame(step), stop: stop };
    }
    function restoreKbComposition() {
      if (!kbSession) return;
      var session = kbSession;
      kbSession = null;
      // Only act if the keyboard genuinely opened AND the composition
      // actually moved - never scroll "just because" a field was focused.
      if (!session.keyboardOpened) return;
      // Anchor on the Stage's own position within the viewport rather than
      // a raw scrollY: if anything above the Stage changed height while the
      // keyboard was open, this still puts the Stage back exactly where the
      // customer last saw it.
      var target = (stage.getBoundingClientRect().top + window.pageYOffset) - session.stageTop;
      if (Math.abs(target - window.pageYOffset) < 4) return;
      animateScrollTo(Math.max(0, target), keyboardReturnMs());
    }
    function waitForViewportSettle() {
      // The keyboard is closed when the visual viewport has grown back to
      // (about) its pre-keyboard height AND stopped changing. iOS keeps
      // adjusting geometry for a while after blur, so this waits for two
      // consecutive stable readings instead of guessing with one timeout.
      var lastH = viewportHeight();
      var stableCount = 0;
      var started = Date.now();
      cancelKbTimers();
      (function poll() {
        kbRaf = requestAnimationFrame(function () {
          if (!kbSession) return;
          var h = viewportHeight();
          var recovered = !kbSession.baseHeight || h >= kbSession.baseHeight - 40;
          stableCount = Math.abs(h - lastH) < 2 ? stableCount + 1 : 0;
          lastH = h;
          if (recovered && stableCount >= 3) { restoreKbComposition(); return; }
          // Bounded safety net, never the primary mechanism: if a browser
          // never reports a stable recovered viewport, restore anyway
          // rather than leaving the customer stranded.
          if (Date.now() - started > 1200) { restoreKbComposition(); return; }
          poll();
        });
      })();
    }
    function onKbFocusIn(ev) {
      if (!mql.matches || !isDedicationInput(ev.target)) return;
      cancelKbTimers();
      if (kbSession) return; // Line 1 -> Line 2 continues the SAME session.
      kbSession = {
        stageTop: stageViewportTop(),
        baseHeight: viewportHeight(),
        keyboardOpened: false,
      };
      // Detect a real keyboard by a meaningful shrink of the visual
      // viewport, not by the focus event alone (an external keyboard, or a
      // desktop browser at mobile width, opens nothing).
      var check = function () {
        if (!kbSession) return;
        if (viewportHeight() <= kbSession.baseHeight - 120) kbSession.keyboardOpened = true;
      };
      if (vv) {
        kbSession.onVvResize = check;
        vv.addEventListener('resize', check);
      } else {
        // No visualViewport (older Android WebView): fall back to the
        // layout viewport, which those browsers DO resize for the keyboard.
        kbSession.onWinResize = check;
        window.addEventListener('resize', check);
      }
      kbSettleTimer = setTimeout(check, 350);
    }
    function endKbSession() {
      if (!kbSession) return;
      if (kbSession.onVvResize && vv) vv.removeEventListener('resize', kbSession.onVvResize);
      if (kbSession.onWinResize) window.removeEventListener('resize', kbSession.onWinResize);
      kbSession.onVvResize = null;
      kbSession.onWinResize = null;
      waitForViewportSettle();
    }
    function onKbFocusOut(ev) {
      if (!kbSession || !isDedicationInput(ev.target)) return;
      cancelKbTimers();
      // Grace period so moving focus Line 1 -> Line 2 (keyboard never
      // closes) is treated as one continuous editing session.
      kbBlurTimer = setTimeout(function () {
        kbBlurTimer = null;
        if (isDedicationInput(document.activeElement)) return;
        endKbSession();
      }, 180);
    }
    root.addEventListener('focusin', onKbFocusIn);
    root.addEventListener('focusout', onKbFocusOut);

    boundRoots.set(root, function teardown() {
      root.removeEventListener('focusin', onKbFocusIn);
      root.removeEventListener('focusout', onKbFocusOut);
      if (kbSession) {
        if (kbSession.onVvResize && vv) vv.removeEventListener('resize', kbSession.onVvResize);
        if (kbSession.onWinResize) window.removeEventListener('resize', kbSession.onWinResize);
        kbSession = null;
      }
      cancelKbTimers();
      cancelKbAnimation();
      clearDockClip(stage.querySelector('[data-ever-leaf-mobile-dock]'));
      if (stateCfgObserver) stateCfgObserver.disconnect();
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', sync);
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(sync); // eslint-disable-line no-restricted-properties -- Safari <14 fallback
      }
      if (observer) { observer.disconnect(); observer = null; }
      if (belowRO) { belowRO.disconnect(); belowRO = null; }
      if (heightRO) { heightRO.disconnect(); heightRO = null; }
      stateMO.disconnect();
      if (rebindObserver) { rebindObserver.disconnect(); rebindObserver = null; }
      if (unsubscribeViewMode) { unsubscribeViewMode(); unsubscribeViewMode = null; }
      document.removeEventListener('ever-leaf:local-photo-ready', onLocalPhotoReady);
      if ('onscrollend' in track) track.removeEventListener('scrollend', clearScrollTarget);
      clearScrollTarget();
      movedNodes = [];
    });

    // CORE VERIFICATION PASS FIX — confirmed via QA: reparenting the View
    // Toggle button synchronously, in the same tick this script's own
    // `defer` execution runs, could happen BEFORE assets/ever-leaf-
    // personalizer.js's own init() has finished setting up the View
    // Toggle's first-appear reveal system for that exact button node —
    // the button never received its data-ever-leaf-view-toggle-visible
    // attribute afterward (Desktop, where the button is never moved,
    // worked correctly; Mobile did not). Deferring by one macrotask lets
    // any other same-tick `defer` script's synchronous init complete
    // first. Still true, still required, still unchanged below.
    //
    // AVOIDABLE-DELAY REMOVAL PASS — everything else structural
    // (Rating/Title/Description/Price/Heading reparenting, Dock/Carousel
    // reveal) does NOT share that race (see moveRealNodesIn()'s own header
    // comment for exactly why) and no longer waits behind it: activated
    // synchronously, right here, on the FIRST activation exactly like every
    // later resize-triggered one already was. Only Toggle/Hints' own move
    // (and their own Initial Appearance pass) stays behind the macrotask,
    // completed by activateMobileToggleHints() once sync() fires.
    // activateMobile(false) below already calls applyMobileReferenceGeometry()
    // as its own first step (synchronously, right here, not deferred), so
    // the first paint of an already-Mobile page never uses the scale-1
    // fallback - no separate standalone call is needed any more.
    if (mql.matches) {
      activateMobile(false);
      firstSyncPending = true;
    }
    setTimeout(sync, 0);
  }

  function initAll() {
    document.querySelectorAll('.ever-leaf-product').forEach(function (root) {
      initStage(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor section reloads re-render this section's markup from
  // scratch (new DOM nodes, new [data-ever-leaf-mobile-target] elements) —
  // re-run init so the new nodes get bound. Mirrors the existing
  // shopify:section:load handling in assets/ever-leaf-personalizer.js.
  // (The "lighter-weight in-place refresh that never fires shopify:
  // section:load at all" case is now self-healed per-root, INSIDE
  // initStage() itself — see the rebindObserver set up right before each
  // root's boundRoots teardown above — rather than by a one-time external
  // scan here, which could only ever observe whatever `.ever-leaf-product__
  // visual` node objects existed at page-load time.)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      var root = event.target && event.target.querySelector ? event.target.querySelector('.ever-leaf-product') : null;
      if (root) initStage(root);
    });
  }
})();
